import { POLITICAL_LEADERS_AGE, calculateAge } from '../data/politicalLeadersAge';
import { COMPLETE_HOUSE_REPRESENTATIVES } from '../data/completeHouseRepresentatives';
import { 
    calculateTenure, 
    getGeneration, 
    getAgeColor, 
    getTenureColor,
    formatAgeDisplay
} from '../utils/politicalHelpers';

class StateGovernmentService {
    constructor() {
        // Official state websites
        this.stateWebsites = {
            'AL': 'https://alabama.gov',
            'AK': 'https://alaska.gov',
            'AZ': 'https://az.gov',
            'AR': 'https://arkansas.gov',
            'CA': 'https://ca.gov',
            'CO': 'https://colorado.gov',
            'CT': 'https://ct.gov',
            'DE': 'https://delaware.gov',
            'FL': 'https://myflorida.com',
            'GA': 'https://georgia.gov',
            'HI': 'https://hawaii.gov',
            'ID': 'https://idaho.gov',
            'IL': 'https://illinois.gov',
            'IN': 'https://in.gov',
            'IA': 'https://iowa.gov',
            'KS': 'https://kansas.gov',
            'KY': 'https://kentucky.gov',
            'LA': 'https://louisiana.gov',
            'ME': 'https://maine.gov',
            'MD': 'https://maryland.gov',
            'MA': 'https://mass.gov',
            'MI': 'https://michigan.gov',
            'MN': 'https://mn.gov',
            'MS': 'https://ms.gov',
            'MO': 'https://mo.gov',
            'MT': 'https://mt.gov',
            'NE': 'https://nebraska.gov',
            'NV': 'https://nv.gov',
            'NH': 'https://nh.gov',
            'NJ': 'https://nj.gov',
            'NM': 'https://newmexico.gov',
            'NY': 'https://ny.gov',
            'NC': 'https://nc.gov',
            'ND': 'https://nd.gov',
            'OH': 'https://ohio.gov',
            'OK': 'https://ok.gov',
            'OR': 'https://oregon.gov',
            'PA': 'https://pa.gov',
            'RI': 'https://ri.gov',
            'SC': 'https://sc.gov',
            'SD': 'https://sd.gov',
            'TN': 'https://tn.gov',
            'TX': 'https://texas.gov',
            'UT': 'https://utah.gov',
            'VT': 'https://vermont.gov',
            'VA': 'https://virginia.gov',
            'WA': 'https://wa.gov',
            'WV': 'https://wv.gov',
            'WI': 'https://wisconsin.gov',
            'WY': 'https://wyo.gov'
        };

        // State Governors (as of 2024)
        this.governors = {
            'AL': { name: 'Kay Ivey', party: 'R', termEnd: '2027', photo: '👤', website: 'https://governor.alabama.gov', age: 80, gender: 'F', birthYear: 1944, tenure: '2017-present' },
            'AK': { name: 'Mike Dunleavy', party: 'R', termEnd: '2026', photo: '👤', website: 'https://gov.alaska.gov', age: 63, gender: 'M', birthYear: 1961, tenure: '2018-present' },
            'AZ': { name: 'Katie Hobbs', party: 'D', termEnd: '2027', photo: '👤', website: 'https://azgovernor.gov', age: 54, gender: 'F', birthYear: 1969, tenure: '2023-present' },
            'AR': { name: 'Sarah Huckabee Sanders', party: 'R', termEnd: '2027', photo: '👤', website: 'https://governor.arkansas.gov', age: 42, gender: 'F', birthYear: 1982, tenure: '2023-present' },
            'CA': { name: 'Gavin Newsom', party: 'D', termEnd: '2027', photo: '👤', website: 'https://gov.ca.gov', age: 57, gender: 'M', birthYear: 1967, tenure: '2019-present' },
            'CO': { name: 'Jared Polis', party: 'D', termEnd: '2027', photo: '👤', website: 'https://governor.colorado.gov', tenure: '2019-present' },
            'CT': { name: 'Ned Lamont', party: 'D', termEnd: '2027', photo: '👤', website: 'https://portal.ct.gov/governor', tenure: '2019-present' },
            'DE': { name: 'John Carney', party: 'D', termEnd: '2025', photo: '👤', website: 'https://governor.delaware.gov', tenure: '2017-present' },
            'FL': { name: 'Ron DeSantis', party: 'R', termEnd: '2027', photo: '👤', website: 'https://flgov.com', age: 46, gender: 'M', birthYear: 1978, tenure: '2019-present' },
            'GA': { name: 'Brian Kemp', party: 'R', termEnd: '2027', photo: '👤', website: 'https://gov.georgia.gov', tenure: '2019-present' },
            'HI': { name: 'Josh Green', party: 'D', termEnd: '2026', photo: '👤', website: 'https://governor.hawaii.gov', tenure: '2022-present' },
            'ID': { name: 'Brad Little', party: 'R', termEnd: '2027', photo: '👤', website: 'https://gov.idaho.gov', tenure: '2019-present' },
            'IL': { name: 'J. B. Pritzker', party: 'D', termEnd: '2027', photo: '👤', website: 'https://www2.illinois.gov/gov', tenure: '2019-present' },
            'IN': { name: 'Eric Holcomb', party: 'R', termEnd: '2025', photo: '👤', website: 'https://www.in.gov/gov', tenure: '2017-present' },
            'IA': { name: 'Kim Reynolds', party: 'R', termEnd: '2027', photo: '👤', website: 'https://governor.iowa.gov', tenure: '2017-present' },
            'KS': { name: 'Laura Kelly', party: 'D', termEnd: '2027', photo: '👤', website: 'https://governor.kansas.gov', tenure: '2019-present' },
            'KY': { name: 'Andy Beshear', party: 'D', termEnd: '2027', photo: '👤', website: 'https://governor.ky.gov', tenure: '2019-present' },
            'LA': { name: 'Jeff Landry', party: 'R', termEnd: '2028', photo: '👤', website: 'https://gov.louisiana.gov', tenure: '2024-present' },
            'ME': { name: 'Janet Mills', party: 'D', termEnd: '2027', photo: '👤', website: 'https://www.maine.gov/governor', tenure: '2019-present' },
            'MD': { name: 'Wes Moore', party: 'D', termEnd: '2027', photo: '👤', website: 'https://governor.maryland.gov', tenure: '2023-present' },
            'MA': { name: 'Maura Healey', party: 'D', termEnd: '2027', photo: '👤', website: 'https://www.mass.gov/governor', tenure: '2023-present' },
            'MI': { name: 'Gretchen Whitmer', party: 'D', termEnd: '2027', photo: '👤', website: 'https://www.michigan.gov/whitmer', tenure: '2019-present' },
            'MN': { name: 'Tim Walz', party: 'D', termEnd: '2027', photo: '👤', website: 'https://mn.gov/governor', tenure: '2019-present' },
            'MS': { name: 'Tate Reeves', party: 'R', termEnd: '2028', photo: '👤', website: 'https://governorreeves.ms.gov', tenure: '2020-present' },
            'MO': { name: 'Mike Parson', party: 'R', termEnd: '2025', photo: '👤', website: 'https://governor.mo.gov', tenure: '2018-present' },
            'MT': { name: 'Greg Gianforte', party: 'R', termEnd: '2025', photo: '👤', website: 'https://governor.mt.gov', tenure: '2021-present' },
            'NE': { name: 'Jim Pillen', party: 'R', termEnd: '2027', photo: '👤', website: 'https://governor.nebraska.gov', tenure: '2023-present' },
            'NV': { name: 'Joe Lombardo', party: 'R', termEnd: '2027', photo: '👤', website: 'https://gov.nv.gov', tenure: '2023-present' },
            'NH': { name: 'Chris Sununu', party: 'R', termEnd: '2025', photo: '👤', website: 'https://www.governor.nh.gov', tenure: '2017-present' },
            'NJ': { name: 'Phil Murphy', party: 'D', termEnd: '2026', photo: '👤', website: 'https://nj.gov/governor', tenure: '2018-present' },
            'NM': { name: 'Michelle Lujan Grisham', party: 'D', termEnd: '2027', photo: '👤', website: 'https://www.governor.state.nm.us', tenure: '2019-present' },
            'NY': { name: 'Kathy Hochul', party: 'D', termEnd: '2027', photo: '👤', website: 'https://www.governor.ny.gov', age: 66, gender: 'F', birthYear: 1958, tenure: '2021-present' },
            'NC': { name: 'Roy Cooper', party: 'D', termEnd: '2025', photo: '👤', website: 'https://governor.nc.gov', tenure: '2017-present' },
            'ND': { name: 'Doug Burgum', party: 'R', termEnd: '2025', photo: '👤', website: 'https://governor.nd.gov', tenure: '2016-present' },
            'OH': { name: 'Mike DeWine', party: 'R', termEnd: '2027', photo: '👤', website: 'https://governor.ohio.gov', tenure: '2019-present' },
            'OK': { name: 'Kevin Stitt', party: 'R', termEnd: '2027', photo: '👤', website: 'https://oklahoma.gov/governor', tenure: '2019-present' },
            'OR': { name: 'Tina Kotek', party: 'D', termEnd: '2027', photo: '👤', website: 'https://www.oregon.gov/gov', tenure: '2023-present' },
            'PA': { name: 'Josh Shapiro', party: 'D', termEnd: '2027', photo: '👤', website: 'https://www.governor.pa.gov', tenure: '2023-present' },
            'RI': { name: 'Dan McKee', party: 'D', termEnd: '2025', photo: '👤', website: 'https://governor.ri.gov', tenure: '2021-present' },
            'SC': { name: 'Henry McMaster', party: 'R', termEnd: '2027', photo: '👤', website: 'https://governor.sc.gov', tenure: '2017-present' },
            'SD': { name: 'Kristi Noem', party: 'R', termEnd: '2027', photo: '👤', website: 'https://governor.sd.gov', tenure: '2019-present' },
            'TN': { name: 'Bill Lee', party: 'R', termEnd: '2027', photo: '👤', website: 'https://www.tn.gov/governor', tenure: '2019-present' },
            'TX': { name: 'Greg Abbott', party: 'R', termEnd: '2027', photo: '👤', website: 'https://gov.texas.gov', age: 67, gender: 'M', birthYear: 1957, tenure: '2015-present' },
            'UT': { name: 'Spencer Cox', party: 'R', termEnd: '2025', photo: '👤', website: 'https://governor.utah.gov', tenure: '2021-present' },
            'VT': { name: 'Phil Scott', party: 'R', termEnd: '2025', photo: '👤', website: 'https://governor.vermont.gov', tenure: '2017-present' },
            'VA': { name: 'Glenn Youngkin', party: 'R', termEnd: '2026', photo: '👤', website: 'https://www.governor.virginia.gov', tenure: '2022-present' },
            'WA': { name: 'Jay Inslee', party: 'D', termEnd: '2025', photo: '👤', website: 'https://www.governor.wa.gov', tenure: '2013-present' },
            'WV': { name: 'Jim Justice', party: 'R', termEnd: '2025', photo: '👤', website: 'https://governor.wv.gov', tenure: '2017-present' },
            'WI': { name: 'Tony Evers', party: 'D', termEnd: '2027', photo: '👤', website: 'https://evers.wi.gov', tenure: '2019-present' },
            'WY': { name: 'Mark Gordon', party: 'R', termEnd: '2027', photo: '👤', website: 'https://governor.wyo.gov', tenure: '2019-present' }
        };

        // U.S. Senators by State (as of 2024)
        this.senators = {
            'AL': [
                { name: 'Tommy Tuberville', party: 'R', termEnd: '2027', website: 'https://www.tuberville.senate.gov', age: 70, gender: 'M', birthYear: 1954, tenure: '2021-present' },
                { name: 'Katie Britt', party: 'R', termEnd: '2029', website: 'https://www.britt.senate.gov', age: 42, gender: 'F', birthYear: 1982, tenure: '2023-present' }
            ],
            'AK': [
                { name: 'Lisa Murkowski', party: 'R', termEnd: '2029', website: 'https://www.murkowski.senate.gov' , gender: 'F', tenure: '2023-present' },
                { name: 'Dan Sullivan', party: 'R', termEnd: '2027', website: 'https://www.sullivan.senate.gov' , gender: 'M', tenure: '2015-present' }
            ],
            'AZ': [
                { name: 'Ruben Gallego', gender: 'M', party: 'D', termEnd: '2025', website: 'https://www.senate.gov/senators/contact', tenure: '2025-present' },
                { name: 'Mark Kelly', party: 'D', termEnd: '2029', website: 'https://www.kelly.senate.gov' , gender: 'M', tenure: '2020-present' }
            ],
            'AR': [
                { name: 'John Boozman', party: 'R', termEnd: '2029', website: 'https://www.boozman.senate.gov' , gender: 'M', tenure: '2011-present' },
                { name: 'Tom Cotton', party: 'R', termEnd: '2027', website: 'https://www.cotton.senate.gov' , gender: 'M', tenure: '2015-present' }
            ],
            'CA': [
                { name: 'Alex Padilla', party: 'D', termEnd: '2029', website: 'https://www.padilla.senate.gov' , gender: 'M', tenure: '2021-present' },
                { name: 'Adam Schiff', gender: 'M', party: 'D', termEnd: '2025', website: 'https://www.senate.gov/senators/contact', tenure: '2025-present' }
            ],
            'CO': [
                { name: 'Michael Bennet', party: 'D', termEnd: '2029', website: 'https://www.bennet.senate.gov' , gender: 'M', tenure: '2009-present' },
                { name: 'John Hickenlooper', party: 'D', termEnd: '2027', website: 'https://www.hickenlooper.senate.gov' , gender: 'M', tenure: '2021-present' }
            ],
            'CT': [
                { name: 'Richard Blumenthal', party: 'D', termEnd: '2029', website: 'https://www.blumenthal.senate.gov' , gender: 'M', tenure: '2011-present' },
                { name: 'Chris Murphy', party: 'D', termEnd: '2025', website: 'https://www.murphy.senate.gov' , gender: 'M', tenure: '2013-present' }
            ],
            'DE': [
                { name: 'Tom Carper', party: 'D', termEnd: '2025', website: 'https://www.carper.senate.gov' , gender: 'M', tenure: '2001-present' },
                { name: 'Chris Coons', party: 'D', termEnd: '2027', website: 'https://www.coons.senate.gov' , gender: 'M', tenure: '2010-present' }
            ],
            'FL': [
                { name: 'Marco Rubio', party: 'R', termEnd: '2029', website: 'https://www.rubio.senate.gov' , gender: 'M', tenure: '2011-present' },
                { name: 'Rick Scott', party: 'R', termEnd: '2025', website: 'https://www.rickscott.senate.gov' , gender: 'M', tenure: '2019-present' }
            ],
            'GA': [
                { name: 'Jon Ossoff', party: 'D', termEnd: '2027', website: 'https://www.ossoff.senate.gov' , gender: 'M', tenure: '2021-present' },
                { name: 'Raphael Warnock', party: 'D', termEnd: '2029', website: 'https://www.warnock.senate.gov' , gender: 'M', tenure: '2021-present' }
            ],
            'HI': [
                { name: 'Brian Schatz', party: 'D', termEnd: '2029', website: 'https://www.schatz.senate.gov' , gender: 'M', tenure: '2012-present' },
                { name: 'Mazie Hirono', party: 'D', termEnd: '2025', website: 'https://www.hirono.senate.gov' , gender: 'F', tenure: '2013-present' }
            ],
            'ID': [
                { name: 'Mike Crapo', party: 'R', termEnd: '2029', website: 'https://www.crapo.senate.gov' , gender: 'M', tenure: '1999-present' },
                { name: 'James Risch', party: 'R', termEnd: '2027', website: 'https://www.risch.senate.gov', gender: 'M', tenure: '2009-present' }
            ],
            'IL': [
                { name: 'Dick Durbin', party: 'D', termEnd: '2027', website: 'https://www.durbin.senate.gov' , gender: 'M', tenure: '1997-present' },
                { name: 'Tammy Duckworth', party: 'D', termEnd: '2029', website: 'https://www.duckworth.senate.gov' , gender: 'F', tenure: '2017-present' }
            ],
            'IN': [
                { name: 'Todd Young', party: 'R', termEnd: '2029', website: 'https://www.young.senate.gov' , gender: 'M', tenure: '2017-present' },
                { name: 'Mike Braun', party: 'R', termEnd: '2025', website: 'https://www.braun.senate.gov' , gender: 'M', tenure: '2019-present' }
            ],
            'IA': [
                { name: 'Chuck Grassley', party: 'R', termEnd: '2029', website: 'https://www.grassley.senate.gov' , gender: 'M', tenure: '1981-present' },
                { name: 'Joni Ernst', party: 'R', termEnd: '2027', website: 'https://www.ernst.senate.gov' , gender: 'F', tenure: '2015-present' }
            ],
            'KS': [
                { name: 'Jerry Moran', party: 'R', termEnd: '2029', website: 'https://www.moran.senate.gov' , gender: 'M', tenure: '2011-present' },
                { name: 'Roger Marshall', party: 'R', termEnd: '2027', website: 'https://www.marshall.senate.gov' , gender: 'M', tenure: '2021-present' }
            ],
            'KY': [
                { name: 'Mitch McConnell', party: 'R', termEnd: '2027', website: 'https://www.mcconnell.senate.gov' , gender: 'M', tenure: '1985-present' },
                { name: 'Rand Paul', party: 'R', termEnd: '2029', website: 'https://www.paul.senate.gov' , gender: 'M', tenure: '2011-present' }
            ],
            'LA': [
                { name: 'Bill Cassidy', party: 'R', termEnd: '2027', website: 'https://www.cassidy.senate.gov' , gender: 'M', tenure: '2015-present' },
                { name: 'John Kennedy', party: 'R', termEnd: '2029', website: 'https://www.kennedy.senate.gov' , gender: 'M', tenure: '2017-present' }
            ],
            'ME': [
                { name: 'Susan Collins', party: 'R', termEnd: '2027', website: 'https://www.collins.senate.gov' , gender: 'F', tenure: '1997-present' },
                { name: 'Angus King', party: 'I', termEnd: '2025', website: 'https://www.king.senate.gov' , gender: 'M', tenure: '2013-present' }
            ],
            'MD': [
                { name: 'Ben Cardin', party: 'D', termEnd: '2025', website: 'https://www.cardin.senate.gov' , gender: 'M', tenure: '2007-present' },
                { name: 'Chris Van Hollen', party: 'D', termEnd: '2029', website: 'https://www.vanhollen.senate.gov' , gender: 'M', tenure: '2017-present' }
            ],
            'MA': [
                { name: 'Elizabeth Warren', party: 'D', termEnd: '2025', website: 'https://www.warren.senate.gov', age: 75, gender: 'F', birthYear: 1949, tenure: '2013-present' },
                { name: 'Ed Markey', party: 'D', termEnd: '2027', website: 'https://www.markey.senate.gov' , gender: 'M', tenure: '2013-present' }
            ],
            'MI': [
                { name: 'Debbie Stabenow', party: 'D', termEnd: '2025', website: 'https://www.stabenow.senate.gov' , gender: 'F', tenure: '2001-present' },
                { name: 'Gary Peters', party: 'D', termEnd: '2027', website: 'https://www.peters.senate.gov' , gender: 'M', tenure: '2015-present' }
            ],
            'MN': [
                { name: 'Amy Klobuchar', party: 'D', termEnd: '2025', website: 'https://www.klobuchar.senate.gov' , gender: 'F', tenure: '2007-present' },
                { name: 'Tina Smith', party: 'D', termEnd: '2027', website: 'https://www.smith.senate.gov' , gender: 'F', tenure: '2018-present' }
            ],
            'MS': [
                { name: 'Roger Wicker', party: 'R', termEnd: '2025', website: 'https://www.wicker.senate.gov' , gender: 'M', tenure: '2007-present' },
                { name: 'Cindy Hyde-Smith', party: 'R', termEnd: '2027', website: 'https://www.hydesmith.senate.gov' , gender: 'F', tenure: '2018-present' }
            ],
            'MO': [
                { name: 'Josh Hawley', party: 'R', termEnd: '2025', website: 'https://www.hawley.senate.gov' , gender: 'M', tenure: '2019-present' },
                { name: 'Eric Schmitt', party: 'R', termEnd: '2029', website: 'https://www.schmitt.senate.gov' , gender: 'M', tenure: '2023-present' }
            ],
            'MT': [
                { name: 'Jon Tester', party: 'D', termEnd: '2025', website: 'https://www.tester.senate.gov' , gender: 'M', tenure: '2007-present' },
                { name: 'Steve Daines', party: 'R', termEnd: '2027', website: 'https://www.daines.senate.gov' , gender: 'M', tenure: '2015-present' }
            ],
            'NE': [
                { name: 'Deb Fischer', party: 'R', termEnd: '2025', website: 'https://www.fischer.senate.gov' , gender: 'F', tenure: '2013-present' },
                { name: 'Pete Ricketts', party: 'R', termEnd: '2029', website: 'https://www.ricketts.senate.gov' , gender: 'M', tenure: '2023-present' }
            ],
            'NV': [
                { name: 'Catherine Cortez Masto', party: 'D', termEnd: '2029', website: 'https://www.cortezmasto.senate.gov' , gender: 'F', tenure: '2017-present' },
                { name: 'Jacky Rosen', party: 'D', termEnd: '2025', website: 'https://www.rosen.senate.gov' , gender: 'F', tenure: '2019-present' }
            ],
            'NH': [
                { name: 'Jeanne Shaheen', party: 'D', termEnd: '2027', website: 'https://www.shaheen.senate.gov' , gender: 'F', tenure: '2009-present' },
                { name: 'Maggie Hassan', party: 'D', termEnd: '2029', website: 'https://www.hassan.senate.gov' , gender: 'F', tenure: '2017-present' }
            ],
            'NJ': [
                { name: 'Bob Menendez', party: 'D', termEnd: '2025', website: 'https://www.menendez.senate.gov' , gender: 'M', tenure: '2006-present' },
                { name: 'Cory Booker', party: 'D', termEnd: '2027', website: 'https://www.booker.senate.gov' , gender: 'M', tenure: '2013-present' }
            ],
            'NM': [
                { name: 'Martin Heinrich', party: 'D', termEnd: '2025', website: 'https://www.heinrich.senate.gov' , gender: 'M', tenure: '2013-present' },
                { name: 'Ben Ray Luján', party: 'D', termEnd: '2027', website: 'https://www.lujan.senate.gov' , gender: 'M', tenure: '2021-present' }
            ],
            'NY': [
                { name: 'Chuck Schumer', party: 'D', termEnd: '2029', website: 'https://www.schumer.senate.gov' , gender: 'M', tenure: '1999-present' },
                { name: 'Kirsten Gillibrand', party: 'D', termEnd: '2025', website: 'https://www.gillibrand.senate.gov' , gender: 'F', tenure: '2009-present' }
            ],
            'NC': [
                { name: 'Thom Tillis', party: 'R', termEnd: '2027', website: 'https://www.tillis.senate.gov' , gender: 'M', tenure: '2015-present' },
                { name: 'Ted Budd', party: 'R', termEnd: '2029', website: 'https://www.budd.senate.gov' , gender: 'M', tenure: '2023-present' }
            ],
            'ND': [
                { name: 'John Hoeven', party: 'R', termEnd: '2029', website: 'https://www.hoeven.senate.gov' , gender: 'M', tenure: '2011-present' },
                { name: 'Kevin Cramer', party: 'R', termEnd: '2025', website: 'https://www.cramer.senate.gov' , gender: 'M', tenure: '2019-present' }
            ],
            'OH': [
                { name: 'Sherrod Brown', party: 'D', termEnd: '2025', website: 'https://www.brown.senate.gov' , gender: 'M', tenure: '2007-present' },
                { name: 'J. D. Vance', party: 'R', termEnd: '2029', website: 'https://www.vance.senate.gov', gender: 'M', tenure: '2023-present' }
            ],
            'OK': [
                { name: 'James Lankford', party: 'R', termEnd: '2029', website: 'https://www.lankford.senate.gov' , gender: 'M', tenure: '2015-present' },
                { name: 'Markwayne Mullin', party: 'R', termEnd: '2027', website: 'https://www.mullin.senate.gov' , gender: 'M', tenure: '2023-present' }
            ],
            'OR': [
                { name: 'Ron Wyden', party: 'D', termEnd: '2029', website: 'https://www.wyden.senate.gov' , gender: 'M', tenure: '1996-present' },
                { name: 'Jeff Merkley', party: 'D', termEnd: '2027', website: 'https://www.merkley.senate.gov' , gender: 'M', tenure: '2009-present' }
            ],
            'PA': [
                { name: 'Bob Casey Jr.', party: 'D', termEnd: '2025', website: 'https://www.casey.senate.gov' , gender: 'M', tenure: '2007-present' },
                { name: 'John Fetterman', party: 'D', termEnd: '2029', website: 'https://www.fetterman.senate.gov' , gender: 'M', tenure: '2023-present' }
            ],
            'RI': [
                { name: 'Jack Reed', party: 'D', termEnd: '2027', website: 'https://www.reed.senate.gov' , gender: 'M', tenure: '1997-present' },
                { name: 'Sheldon Whitehouse', party: 'D', termEnd: '2025', website: 'https://www.whitehouse.senate.gov' , gender: 'M', tenure: '2007-present' }
            ],
            'SC': [
                { name: 'Lindsey Graham', party: 'R', termEnd: '2027', website: 'https://www.lgraham.senate.gov' , gender: 'M', tenure: '2003-present' },
                { name: 'Tim Scott', party: 'R', termEnd: '2029', website: 'https://www.scott.senate.gov' , gender: 'M', tenure: '2013-present' }
            ],
            'SD': [
                { name: 'John Thune', party: 'R', termEnd: '2029', website: 'https://www.thune.senate.gov' , gender: 'M', tenure: '2005-present' },
                { name: 'Mike Rounds', party: 'R', termEnd: '2027', website: 'https://www.rounds.senate.gov' , gender: 'M', tenure: '2015-present' }
            ],
            'TN': [
                { name: 'Marsha Blackburn', party: 'R', termEnd: '2025', website: 'https://www.blackburn.senate.gov' , gender: 'F', tenure: '2019-present' },
                { name: 'Bill Hagerty', party: 'R', termEnd: '2027', website: 'https://www.hagerty.senate.gov' , gender: 'M', tenure: '2021-present' }
            ],
            'TX': [
                { name: 'John Cornyn', party: 'R', termEnd: '2027', website: 'https://www.cornyn.senate.gov', age: 72, gender: 'M', birthYear: 1952, tenure: '2002-present' },
                { name: 'Ted Cruz', party: 'R', termEnd: '2025', website: 'https://www.cruz.senate.gov', age: 54, gender: 'M', birthYear: 1970, tenure: '2013-present' }
            ],
            'UT': [
                { name: 'Mike Lee', party: 'R', termEnd: '2029', website: 'https://www.lee.senate.gov' , gender: 'M', tenure: '2011-present' },
                { name: 'Mitt Romney', party: 'R', termEnd: '2025', website: 'https://www.romney.senate.gov' , gender: 'M', tenure: '2019-present' }
            ],
            'VT': [
                { name: 'Bernie Sanders', party: 'I', termEnd: '2025', website: 'https://www.sanders.senate.gov', age: 83, gender: 'M', birthYear: 1941, tenure: '2007-present' },
                { name: 'Peter Welch', party: 'D', termEnd: '2029', website: 'https://www.welch.senate.gov' , gender: 'M', tenure: '2023-present' }
            ],
            'VA': [
                { name: 'Tim Kaine', party: 'D', termEnd: '2025', website: 'https://www.kaine.senate.gov' , gender: 'M', tenure: '2013-present' },
                { name: 'Mark Warner', party: 'D', termEnd: '2027', website: 'https://www.warner.senate.gov' , gender: 'M', tenure: '2009-present' }
            ],
            'WA': [
                { name: 'Patty Murray', party: 'D', termEnd: '2029', website: 'https://www.murray.senate.gov' , gender: 'F', tenure: '1993-present' },
                { name: 'Maria Cantwell', party: 'D', termEnd: '2025', website: 'https://www.cantwell.senate.gov' , gender: 'F', tenure: '2001-present' }
            ],
            'WV': [
                { name: 'Joe Manchin', party: 'D', termEnd: '2025', website: 'https://www.manchin.senate.gov' , gender: 'M', tenure: '2010-present' },
                { name: 'Shelley Moore Capito', party: 'R', termEnd: '2027', website: 'https://www.capito.senate.gov' , gender: 'F', tenure: '2015-present' }
            ],
            'WI': [
                { name: 'Ron Johnson', party: 'R', termEnd: '2029', website: 'https://www.ronjohnson.senate.gov' , gender: 'M', tenure: '2011-present' },
                { name: 'Tammy Baldwin', party: 'D', termEnd: '2025', website: 'https://www.baldwin.senate.gov' , gender: 'F', tenure: '2013-present' }
            ],
            'WY': [
                { name: 'John Barrasso', party: 'R', termEnd: '2025', website: 'https://www.barrasso.senate.gov' , gender: 'M', tenure: '2007-present' },
                { name: 'Cynthia Lummis', party: 'R', termEnd: '2027', website: 'https://www.lummis.senate.gov' , gender: 'F', tenure: '2021-present' }
            ]
        };

        // Number of U.S. House seats per state
        this.houseSeats = {
            'AL': 7, 'AK': 1, 'AZ': 9, 'AR': 4, 'CA': 52, 'CO': 8, 'CT': 5, 'DE': 1,
            'FL': 28, 'GA': 14, 'HI': 2, 'ID': 2, 'IL': 17, 'IN': 9, 'IA': 4, 'KS': 4,
            'KY': 6, 'LA': 6, 'ME': 2, 'MD': 8, 'MA': 9, 'MI': 13, 'MN': 8, 'MS': 4,
            'MO': 8, 'MT': 2, 'NE': 3, 'NV': 4, 'NH': 2, 'NJ': 12, 'NM': 3, 'NY': 26,
            'NC': 14, 'ND': 1, 'OH': 15, 'OK': 5, 'OR': 6, 'PA': 17, 'RI': 2, 'SC': 7,
            'SD': 1, 'TN': 9, 'TX': 38, 'UT': 4, 'VT': 1, 'VA': 11, 'WA': 10, 'WV': 2,
            'WI': 8, 'WY': 1
        };

        // State Legislature Control
        this.stateLegislatures = {
            'AL': { senate: 'R', house: 'R', trifecta: 'R' },
            'AK': { senate: 'Coalition', house: 'Coalition', trifecta: null },
            'AZ': { senate: 'R', house: 'R', trifecta: null },
            'AR': { senate: 'R', house: 'R', trifecta: 'R' },
            'CA': { senate: 'D', house: 'D', trifecta: 'D' },
            'CO': { senate: 'D', house: 'D', trifecta: 'D' },
            'CT': { senate: 'D', house: 'D', trifecta: 'D' },
            'DE': { senate: 'D', house: 'D', trifecta: 'D' },
            'FL': { senate: 'R', house: 'R', trifecta: 'R' },
            'GA': { senate: 'R', house: 'R', trifecta: 'R' },
            'HI': { senate: 'D', house: 'D', trifecta: 'D' },
            'ID': { senate: 'R', house: 'R', trifecta: 'R' },
            'IL': { senate: 'D', house: 'D', trifecta: 'D' },
            'IN': { senate: 'R', house: 'R', trifecta: 'R' },
            'IA': { senate: 'R', house: 'R', trifecta: 'R' },
            'KS': { senate: 'R', house: 'R', trifecta: null },
            'KY': { senate: 'R', house: 'R', trifecta: null },
            'LA': { senate: 'R', house: 'R', trifecta: 'R' },
            'ME': { senate: 'D', house: 'D', trifecta: 'D' },
            'MD': { senate: 'D', house: 'D', trifecta: 'D' },
            'MA': { senate: 'D', house: 'D', trifecta: 'D' },
            'MI': { senate: 'D', house: 'D', trifecta: 'D' },
            'MN': { senate: 'D', house: 'D', trifecta: 'D' },
            'MS': { senate: 'R', house: 'R', trifecta: 'R' },
            'MO': { senate: 'R', house: 'R', trifecta: 'R' },
            'MT': { senate: 'R', house: 'R', trifecta: 'R' },
            'NE': { senate: 'Nonpartisan', house: null, trifecta: 'R' },
            'NV': { senate: 'D', house: 'D', trifecta: null },
            'NH': { senate: 'R', house: 'R', trifecta: 'R' },
            'NJ': { senate: 'D', house: 'D', trifecta: 'D' },
            'NM': { senate: 'D', house: 'D', trifecta: 'D' },
            'NY': { senate: 'D', house: 'D', trifecta: 'D' },
            'NC': { senate: 'R', house: 'R', trifecta: null },
            'ND': { senate: 'R', house: 'R', trifecta: 'R' },
            'OH': { senate: 'R', house: 'R', trifecta: 'R' },
            'OK': { senate: 'R', house: 'R', trifecta: 'R' },
            'OR': { senate: 'D', house: 'D', trifecta: 'D' },
            'PA': { senate: 'R', house: 'D', trifecta: null },
            'RI': { senate: 'D', house: 'D', trifecta: 'D' },
            'SC': { senate: 'R', house: 'R', trifecta: 'R' },
            'SD': { senate: 'R', house: 'R', trifecta: 'R' },
            'TN': { senate: 'R', house: 'R', trifecta: 'R' },
            'TX': { senate: 'R', house: 'R', trifecta: 'R' },
            'UT': { senate: 'R', house: 'R', trifecta: 'R' },
            'VT': { senate: 'D', house: 'D', trifecta: null },
            'VA': { senate: 'D', house: 'R', trifecta: null },
            'WA': { senate: 'D', house: 'D', trifecta: 'D' },
            'WV': { senate: 'R', house: 'R', trifecta: 'R' },
            'WI': { senate: 'R', house: 'R', trifecta: null },
            'WY': { senate: 'R', house: 'R', trifecta: 'R' }
        };

                // U.S. House Representatives by State (sample for each state)
        // Note: This shows key representatives. Full data would include all 435 members
        this.houseRepresentatives = {
            'AL': [
                {
                    'name': 'Jerry Carl', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://carl.house.gov'
                },
                {
                    'name': 'Barry Moore', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://barrymoore.house.gov'
                },
                {
                    'name': 'Mike Rogers', gender: 'M',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2003-present',
                    'website': 'https://mikerogers.house.gov'
                },
                {
                    'name': 'Robert Aderholt', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '1997-present',
                    'website': 'https://aderholt.house.gov'
                },
                {
                    'name': 'Dale Strong', gender: 'M',
                    'party': 'R',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://strong.house.gov'
                },
                {
                    'name': 'Gary Palmer', gender: 'M',
                    'party': 'R',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://palmer.house.gov'
                },
                {
                    'name': 'Terri Sewell', gender: 'F',
                    'party': 'D',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://sewell.house.gov'
                }
            ],
            'AK': [
                {
                    'name': 'Mary Peltola', gender: 'F',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2022-present',
                    'website': 'https://peltola.house.gov'
                }
            ],
            'AZ': [
                {
                    'name': 'David Schweikert', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://schweikert.house.gov'
                },
                {
                    'name': 'Eli Crane', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://crane.house.gov'
                },
                {
                    'name': 'Ruben Gallego', gender: 'M',
                    'party': 'D',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://gallego.house.gov'
                },
                {
                    'name': 'Greg Stanton', gender: 'M',
                    'party': 'D',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://stanton.house.gov'
                },
                {
                    'name': 'Andy Biggs', gender: 'M',
                    'party': 'R',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://biggs.house.gov'
                },
                {
                    'name': 'Juan Ciscomani', gender: 'M',
                    'party': 'R',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://ciscomani.house.gov'
                },
                {
                    'name': 'Raúl Grijalva', gender: 'M',
                    'party': 'D',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2003-present',
                    'website': 'https://grijalva.house.gov'
                },
                {
                    'name': 'Debbie Lesko', gender: 'F',
                    'party': 'R',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2018-present',
                    'website': 'https://lesko.house.gov'
                },
                {
                    'name': 'Paul Gosar', gender: 'M',
                    'party': 'R',
                    'district': 9,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://gosar.house.gov'
                }
            ],
            'AR': [
                {
                    'name': 'Rick Crawford', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://crawford.house.gov'
                },
                {
                    'name': 'French Hill', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://hill.house.gov'
                },
                {
                    'name': 'Steve Womack', gender: 'M',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://womack.house.gov'
                },
                {
                    'name': 'Bruce Westerman', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://westerman.house.gov'
                }
            ],
            'CA': [
                {
                    'name': 'Doug LaMalfa', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://lamalfa.house.gov'
                },
                {
                    'name': 'Jared Huffman', gender: 'M',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://huffman.house.gov'
                },
                {
                    'name': 'Kevin Kiley', gender: 'M',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://kiley.house.gov'
                },
                {
                    'name': 'Tom McClintock', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2009-present',
                    'website': 'https://mcclintock.house.gov'
                },
                {
                    'name': 'Mike Thompson', gender: 'M',
                    'party': 'D',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '1999-present',
                    'website': 'https://mikethompson.house.gov'
                },
                {
                    'name': 'Ami Bera', gender: 'M',
                    'party': 'D',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://bera.house.gov'
                },
                {
                    'name': 'Doris Matsui', gender: 'F',
                    'party': 'D',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2005-present',
                    'website': 'https://matsui.house.gov'
                },
                {
                    'name': 'John Garamendi', gender: 'M',
                    'party': 'D',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2009-present',
                    'website': 'https://garamendi.house.gov'
                },
                {
                    'name': 'Josh Harder', gender: 'M',
                    'party': 'D',
                    'district': 9,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://harder.house.gov'
                },
                {
                    'name': 'Mark DeSaulnier', gender: 'M',
                    'party': 'D',
                    'district': 10,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://desaulnier.house.gov'
                },
                {
                    'name': 'Nancy Pelosi', gender: 'F',
                    'party': 'D',
                    'district': 11,
                    'termEnd': '2025',
                    'tenure': '1987-present',
                    'website': 'https://pelosi.house.gov'
                },
                {
                    'name': 'Barbara Lee', gender: 'F',
                    'party': 'D',
                    'district': 12,
                    'termEnd': '2025',
                    'tenure': '1998-present',
                    'website': 'https://lee.house.gov'
                },
                {
                    'name': 'John Duarte', gender: 'M',
                    'party': 'R',
                    'district': 13,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://duarte.house.gov'
                },
                {
                    'name': 'Eric Swalwell', gender: 'M',
                    'party': 'D',
                    'district': 14,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://swalwell.house.gov'
                },
                {
                    'name': 'Anna Eshoo', gender: 'F',
                    'party': 'D',
                    'district': 15,
                    'termEnd': '2025',
                    'tenure': '1993-present',
                    'website': 'https://eshoo.house.gov'
                },
                {
                    'name': 'Zoe Lofgren', gender: 'F',
                    'party': 'D',
                    'district': 16,
                    'termEnd': '2025',
                    'tenure': '1995-present',
                    'website': 'https://lofgren.house.gov'
                },
                {
                    'name': 'Ro Khanna', gender: 'M',
                    'party': 'D',
                    'district': 17,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://khanna.house.gov'
                },
                {
                    'name': 'Jimmy Panetta', gender: 'M',
                    'party': 'D',
                    'district': 18,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://panetta.house.gov'
                },
                {
                    'name': 'Jim Costa', gender: 'M',
                    'party': 'D',
                    'district': 19,
                    'termEnd': '2025',
                    'tenure': '2005-present',
                    'website': 'https://costa.house.gov'
                },
                {
                    'name': 'Kevin McCarthy', gender: 'M',
                    'party': 'R',
                    'district': 20,
                    'termEnd': '2025',
                    'tenure': '2007-2023',
                    'website': 'https://kevinmccarthy.house.gov'
                },
                {
                    'name': 'David Valadao', gender: 'M',
                    'party': 'R',
                    'district': 21,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://valadao.house.gov'
                },
                {
                    'name': 'Rudy Salas',
                    'party': 'D',
                    'district': 22,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://salas.house.gov'
                },
                {
                    'name': 'Jay Obernolte', gender: 'M',
                    'party': 'R',
                    'district': 23,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://obernolte.house.gov'
                },
                {
                    'name': 'Salud Carbajal', gender: 'M',
                    'party': 'D',
                    'district': 24,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://carbajal.house.gov'
                },
                {
                    'name': 'Raul Ruiz', gender: 'M',
                    'party': 'D',
                    'district': 25,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://ruiz.house.gov'
                },
                {
                    'name': 'Julia Brownley', gender: 'F',
                    'party': 'D',
                    'district': 26,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://juliabrownley.house.gov'
                },
                {
                    'name': 'Mike Garcia', gender: 'M',
                    'party': 'R',
                    'district': 27,
                    'termEnd': '2025',
                    'tenure': '2020-present',
                    'website': 'https://mikegarcia.house.gov'
                },
                {
                    'name': 'Judy Chu', gender: 'F',
                    'party': 'D',
                    'district': 28,
                    'termEnd': '2025',
                    'tenure': '2009-present',
                    'website': 'https://chu.house.gov'
                },
                {
                    'name': 'Tony Cárdenas', gender: 'M',
                    'party': 'D',
                    'district': 29,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://cardenas.house.gov'
                },
                {
                    'name': 'Adam Schiff', gender: 'M',
                    'party': 'D',
                    'district': 30,
                    'termEnd': '2025',
                    'tenure': '2001-present',
                    'website': 'https://schiff.house.gov'
                },
                {
                    'name': 'Grace Napolitano', gender: 'F',
                    'party': 'D',
                    'district': 31,
                    'termEnd': '2025',
                    'tenure': '1999-present',
                    'website': 'https://napolitano.house.gov'
                },
                {
                    'name': 'Brad Sherman', gender: 'M',
                    'party': 'D',
                    'district': 32,
                    'termEnd': '2025',
                    'tenure': '1997-present',
                    'website': 'https://sherman.house.gov'
                },
                {
                    'name': 'Pete Aguilar', gender: 'M',
                    'party': 'D',
                    'district': 33,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://aguilar.house.gov'
                },
                {
                    'name': 'Norma Torres', gender: 'F',
                    'party': 'D',
                    'district': 34,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://torres.house.gov'
                },
                {
                    'name': 'Linda Sánchez', gender: 'F',
                    'party': 'D',
                    'district': 35,
                    'termEnd': '2025',
                    'tenure': '2003-present',
                    'website': 'https://lindasanchez.house.gov'
                },
                {
                    'name': 'Ted Lieu',
                    'party': 'D',
                    'district': 36,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://lieu.house.gov'
                },
                {
                    'name': 'Sydney Kamlager-Dove', gender: 'F',
                    'party': 'D',
                    'district': 37,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://kamlager-dove.house.gov'
                },
                {
                    'name': 'Robert Garcia', gender: 'M',
                    'party': 'D',
                    'district': 38,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://robertgarcia.house.gov'
                },
                {
                    'name': 'Mark Takano', gender: 'M',
                    'party': 'D',
                    'district': 39,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://takano.house.gov'
                },
                {
                    'name': 'Young Kim', gender: 'F',
                    'party': 'R',
                    'district': 40,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://youngkim.house.gov'
                },
                {
                    'name': 'Ken Calvert', gender: 'M',
                    'party': 'R',
                    'district': 41,
                    'termEnd': '2025',
                    'tenure': '1993-present',
                    'website': 'https://calvert.house.gov'
                },
                {
                    'name': 'Maxine Waters', gender: 'F',
                    'party': 'D',
                    'district': 42,
                    'termEnd': '2025',
                    'tenure': '1991-present',
                    'website': 'https://waters.house.gov'
                },
                {
                    'name': 'John Garamendi', gender: 'M',
                    'party': 'D',
                    'district': 43,
                    'termEnd': '2025',
                    'tenure': '2009-present',
                    'website': 'https://garamendi.house.gov'
                },
                {
                    'name': 'Nanette Barragán', gender: 'F',
                    'party': 'D',
                    'district': 44,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://barragan.house.gov'
                },
                {
                    'name': 'Michelle Steel', gender: 'F',
                    'party': 'R',
                    'district': 45,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://steel.house.gov'
                },
                {
                    'name': 'Lou Correa', gender: 'M',
                    'party': 'D',
                    'district': 46,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://correa.house.gov'
                },
                {
                    'name': 'Katie Porter', gender: 'F',
                    'party': 'D',
                    'district': 47,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://porter.house.gov'
                },
                {
                    'name': 'Darrell Issa', gender: 'M',
                    'party': 'R',
                    'district': 48,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://issa.house.gov'
                },
                {
                    'name': 'Mike Levin', gender: 'M',
                    'party': 'D',
                    'district': 49,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://mikelevin.house.gov'
                },
                {
                    'name': 'Scott Peters', gender: 'M',
                    'party': 'D',
                    'district': 50,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://scottpeters.house.gov'
                },
                {
                    'name': 'Sara Jacobs', gender: 'F',
                    'party': 'D',
                    'district': 51,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://sarajacobs.house.gov'
                },
                {
                    'name': 'Juan Vargas', gender: 'M',
                    'party': 'D',
                    'district': 52,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://vargas.house.gov'
                }
            ],
            'CO': [
                {
                    'name': 'Diana DeGette', gender: 'F',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '1997-present',
                    'website': 'https://degette.house.gov'
                },
                {
                    'name': 'Joe Neguse', gender: 'M',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://neguse.house.gov'
                },
                {
                    'name': 'Lauren Boebert', gender: 'F',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://boebert.house.gov'
                },
                {
                    'name': 'Ken Buck', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://buck.house.gov'
                },
                {
                    'name': 'Doug Lamborn', gender: 'M',
                    'party': 'R',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2007-present',
                    'website': 'https://lamborn.house.gov'
                },
                {
                    'name': 'Jason Crow', gender: 'M',
                    'party': 'D',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://crow.house.gov'
                },
                {
                    'name': 'Brittany Pettersen', gender: 'F',
                    'party': 'D',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://pettersen.house.gov'
                },
                {
                    'name': 'Yadira Caraveo', gender: 'F',
                    'party': 'D',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://caraveo.house.gov'
                }
            ],
            'CT': [
                {
                    'name': 'John Larson', gender: 'M',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '1999-present',
                    'website': 'https://larson.house.gov'
                },
                {
                    'name': 'Joe Courtney', gender: 'M',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2007-present',
                    'website': 'https://courtney.house.gov'
                },
                {
                    'name': 'Rosa DeLauro', gender: 'F',
                    'party': 'D',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '1991-present',
                    'website': 'https://delauro.house.gov'
                },
                {
                    'name': 'Jim Himes', gender: 'M',
                    'party': 'D',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2009-present',
                    'website': 'https://himes.house.gov'
                },
                {
                    'name': 'Jahana Hayes', gender: 'F',
                    'party': 'D',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://hayes.house.gov'
                }
            ],
            'DE': [
                {
                    'name': 'Lisa Blunt Rochester', gender: 'F',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://bluntrochester.house.gov'
                }
            ],
            'FL': [
                {
                    'name': 'Matt Gaetz', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://gaetz.house.gov'
                },
                {
                    'name': 'Neal Dunn', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://dunn.house.gov'
                },
                {
                    'name': 'Kat Cammack', gender: 'F',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://cammack.house.gov'
                },
                {
                    'name': 'Aaron Bean', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://bean.house.gov'
                },
                {
                    'name': 'John Rutherford', gender: 'M',
                    'party': 'R',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://rutherford.house.gov'
                },
                {
                    'name': 'Michael Waltz', gender: 'M',
                    'party': 'R',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://waltz.house.gov'
                },
                {
                    'name': 'Cory Mills', gender: 'M',
                    'party': 'R',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://mills.house.gov'
                },
                {
                    'name': 'Bill Posey', gender: 'M',
                    'party': 'R',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2009-present',
                    'website': 'https://posey.house.gov'
                },
                {
                    'name': 'Darren Soto', gender: 'M',
                    'party': 'D',
                    'district': 9,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://soto.house.gov'
                },
                {
                    'name': 'Maxwell Frost', gender: 'M',
                    'party': 'D',
                    'district': 10,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://frost.house.gov'
                },
                {
                    'name': 'Daniel Webster', gender: 'M',
                    'party': 'R',
                    'district': 11,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://webster.house.gov'
                },
                {
                    'name': 'Gus Bilirakis', gender: 'M',
                    'party': 'R',
                    'district': 12,
                    'termEnd': '2025',
                    'tenure': '2007-present',
                    'website': 'https://bilirakis.house.gov'
                },
                {
                    'name': 'Anna Paulina Luna', gender: 'F',
                    'party': 'R',
                    'district': 13,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://luna.house.gov'
                },
                {
                    'name': 'Kathy Castor', gender: 'F',
                    'party': 'D',
                    'district': 14,
                    'termEnd': '2025',
                    'tenure': '2007-present',
                    'website': 'https://castor.house.gov'
                },
                {
                    'name': 'Laurel Lee', gender: 'F',
                    'party': 'R',
                    'district': 15,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://laurellee.house.gov'
                },
                {
                    'name': 'Vern Buchanan', gender: 'M',
                    'party': 'R',
                    'district': 16,
                    'termEnd': '2025',
                    'tenure': '2007-present',
                    'website': 'https://buchanan.house.gov'
                },
                {
                    'name': 'Greg Steube', gender: 'M',
                    'party': 'R',
                    'district': 17,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://steube.house.gov'
                },
                {
                    'name': 'Scott Franklin', gender: 'M',
                    'party': 'R',
                    'district': 18,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://franklin.house.gov'
                },
                {
                    'name': 'Byron Donalds', gender: 'M',
                    'party': 'R',
                    'district': 19,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://donalds.house.gov'
                },
                {
                    'name': 'Sheila Cherfilus-McCormick', gender: 'F',
                    'party': 'D',
                    'district': 20,
                    'termEnd': '2025',
                    'tenure': '2022-present',
                    'website': 'https://cherfilus-mccormick.house.gov'
                },
                {
                    'name': 'Brian Mast', gender: 'M',
                    'party': 'R',
                    'district': 21,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://mast.house.gov'
                },
                {
                    'name': 'Lois Frankel', gender: 'F',
                    'party': 'D',
                    'district': 22,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://frankel.house.gov'
                },
                {
                    'name': 'Jared Moskowitz', gender: 'M',
                    'party': 'D',
                    'district': 23,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://moskowitz.house.gov'
                },
                {
                    'name': 'Frederica Wilson', gender: 'F',
                    'party': 'D',
                    'district': 24,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://wilson.house.gov'
                },
                {
                    'name': 'Debbie Wasserman Schultz', gender: 'F',
                    'party': 'D',
                    'district': 25,
                    'termEnd': '2025',
                    'tenure': '2005-present',
                    'website': 'https://wassermanschultz.house.gov'
                },
                {
                    'name': 'Mario Díaz-Balart',
                    'party': 'R',
                    'district': 26,
                    'termEnd': '2025',
                    'tenure': '2003-present',
                    'website': 'https://mariodiazbalart.house.gov'
                },
                {
                    'name': 'María Elvira Salazar',
                    'party': 'R',
                    'district': 27,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://salazar.house.gov'
                },
                {
                    'name': 'Carlos Giménez',
                    'party': 'R',
                    'district': 28,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://gimenez.house.gov'
                }
            ],
            'GA': [
                {
                    'name': 'Buddy Carter', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://buddycarter.house.gov'
                },
                {
                    'name': 'Sanford Bishop', gender: 'M',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '1993-present',
                    'website': 'https://bishop.house.gov'
                },
                {
                    'name': 'Drew Ferguson', gender: 'M',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://ferguson.house.gov'
                },
                {
                    'name': 'Hank Johnson', gender: 'M',
                    'party': 'D',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2007-present',
                    'website': 'https://hankjohnson.house.gov'
                },
                {
                    'name': 'Nikema Williams', gender: 'F',
                    'party': 'D',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://williams.house.gov'
                },
                {
                    'name': 'Rich McCormick', gender: 'M',
                    'party': 'R',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://mccormick.house.gov'
                },
                {
                    'name': 'Lucy McBath', gender: 'F',
                    'party': 'D',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://mcbath.house.gov'
                },
                {
                    'name': 'Austin Scott', gender: 'M',
                    'party': 'R',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://austinscott.house.gov'
                },
                {
                    'name': 'Andrew Clyde', gender: 'M',
                    'party': 'R',
                    'district': 9,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://clyde.house.gov'
                },
                {
                    'name': 'Mike Collins', gender: 'M',
                    'party': 'R',
                    'district': 10,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://mikecollins.house.gov'
                },
                {
                    'name': 'Barry Loudermilk', gender: 'M',
                    'party': 'R',
                    'district': 11,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://loudermilk.house.gov'
                },
                {
                    'name': 'Rick Allen', gender: 'M',
                    'party': 'R',
                    'district': 12,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://allen.house.gov'
                },
                {
                    'name': 'David Scott', gender: 'M',
                    'party': 'D',
                    'district': 13,
                    'termEnd': '2025',
                    'tenure': '2003-present',
                    'website': 'https://davidscott.house.gov'
                },
                {
                    'name': 'Marjorie Taylor Greene', gender: 'F',
                    'party': 'R',
                    'district': 14,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://greene.house.gov'
                }
            ],
            'HI': [
                {
                    'name': 'Ed Case', gender: 'M',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://case.house.gov'
                },
                {
                    'name': 'Jill Tokuda', gender: 'F',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://tokuda.house.gov'
                }
            ],
            'ID': [
                {
                    'name': 'Russ Fulcher', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://fulcher.house.gov'
                },
                {
                    'name': 'Mike Simpson', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '1999-present',
                    'website': 'https://simpson.house.gov'
                }
            ],
            'IL': [
                {
                    'name': 'Jonathan Jackson', gender: 'M',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://jackson.house.gov'
                },
                {
                    'name': 'Robin Kelly', gender: 'F',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://robinkelly.house.gov'
                },
                {
                    'name': 'Delia Ramirez', gender: 'F',
                    'party': 'D',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://ramirez.house.gov'
                },
                {
                    'name': 'Jesús García', gender: 'M',
                    'party': 'D',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://chuygarcia.house.gov'
                },
                {
                    'name': 'Mike Quigley', gender: 'M',
                    'party': 'D',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2009-present',
                    'website': 'https://quigley.house.gov'
                },
                {
                    'name': 'Sean Casten', gender: 'M',
                    'party': 'D',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://casten.house.gov'
                },
                {
                    'name': 'Danny K. Davis',
                    'party': 'D',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '1997-present',
                    'website': 'https://davis.house.gov'
                },
                {
                    'name': 'Raja Krishnamoorthi', gender: 'M',
                    'party': 'D',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://krishnamoorthi.house.gov'
                },
                {
                    'name': 'Jan Schakowsky', gender: 'F',
                    'party': 'D',
                    'district': 9,
                    'termEnd': '2025',
                    'tenure': '1999-present',
                    'website': 'https://schakowsky.house.gov'
                },
                {
                    'name': 'Brad Schneider', gender: 'M',
                    'party': 'D',
                    'district': 10,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://schneider.house.gov'
                },
                {
                    'name': 'Bill Foster', gender: 'M',
                    'party': 'D',
                    'district': 11,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://foster.house.gov'
                },
                {
                    'name': 'Mike Bost', gender: 'M',
                    'party': 'R',
                    'district': 12,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://bost.house.gov'
                },
                {
                    'name': 'Nikki Budzinski', gender: 'F',
                    'party': 'D',
                    'district': 13,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://budzinski.house.gov'
                },
                {
                    'name': 'Lauren Underwood', gender: 'F',
                    'party': 'D',
                    'district': 14,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://underwood.house.gov'
                },
                {
                    'name': 'Mary Miller', gender: 'F',
                    'party': 'R',
                    'district': 15,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://marymiller.house.gov'
                },
                {
                    'name': 'Darin LaHood', gender: 'M',
                    'party': 'R',
                    'district': 16,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://lahood.house.gov'
                },
                {
                    'name': 'Eric Sorensen', gender: 'M',
                    'party': 'D',
                    'district': 17,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://sorensen.house.gov'
                }
            ],
            'IN': [
                {
                    'name': 'Frank Mrvan', gender: 'M',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://mrvan.house.gov'
                },
                {
                    'name': 'Rudy Yakym', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2022-present',
                    'website': 'https://yakym.house.gov'
                },
                {
                    'name': 'Jim Banks', gender: 'M',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://banks.house.gov'
                },
                {
                    'name': 'Jim Baird', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://baird.house.gov'
                },
                {
                    'name': 'Victoria Spartz', gender: 'F',
                    'party': 'R',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://spartz.house.gov'
                },
                {
                    'name': 'Greg Pence', gender: 'M',
                    'party': 'R',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://pence.house.gov'
                },
                {
                    'name': 'André Carson', gender: 'M',
                    'party': 'D',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2008-present',
                    'website': 'https://carson.house.gov'
                },
                {
                    'name': 'Larry Bucshon', gender: 'M',
                    'party': 'R',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://bucshon.house.gov'
                },
                {
                    'name': 'Erin Houchin', gender: 'F',
                    'party': 'R',
                    'district': 9,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://houchin.house.gov'
                }
            ],
            'IA': [
                {
                    'name': 'Mariannette Miller-Meeks', gender: 'F',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://millermeeks.house.gov'
                },
                {
                    'name': 'Ashley Hinson', gender: 'F',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://hinson.house.gov'
                },
                {
                    'name': 'Zach Nunn', gender: 'M',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://nunn.house.gov'
                },
                {
                    'name': 'Randy Feenstra', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://feenstra.house.gov'
                }
            ],
            'KS': [
                {
                    'name': 'Tracey Mann', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://mann.house.gov'
                },
                {
                    'name': 'Jake LaTurner', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://laturner.house.gov'
                },
                {
                    'name': 'Sharice Davids', gender: 'F',
                    'party': 'D',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://davids.house.gov'
                },
                {
                    'name': 'Ron Estes', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://estes.house.gov'
                }
            ],
            'KY': [
                {
                    'name': 'James Comer', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2016-present',
                    'website': 'https://comer.house.gov'
                },
                {
                    'name': 'Brett Guthrie', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2009-present',
                    'website': 'https://guthrie.house.gov'
                },
                {
                    'name': 'Morgan McGarvey', gender: 'M',
                    'party': 'D',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://mcgarvey.house.gov'
                },
                {
                    'name': 'Thomas Massie', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2012-present',
                    'website': 'https://massie.house.gov'
                },
                {
                    'name': 'Hal Rogers', gender: 'M',
                    'party': 'R',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '1981-present',
                    'website': 'https://halrogers.house.gov'
                },
                {
                    'name': 'Andy Barr', gender: 'M',
                    'party': 'R',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://barr.house.gov'
                }
            ],
            'LA': [
                {
                    'name': 'Steve Scalise', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2008-present',
                    'website': 'https://scalise.house.gov'
                },
                {
                    'name': 'Troy Carter', gender: 'M',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://troycarter.house.gov'
                },
                {
                    'name': 'Clay Higgins', gender: 'M',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://clayhiggins.house.gov'
                },
                {
                    'name': 'Mike Johnson', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://mikejohnson.house.gov'
                },
                {
                    'name': 'Julia Letlow', gender: 'F',
                    'party': 'R',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://letlow.house.gov'
                },
                {
                    'name': 'Garret Graves', gender: 'M',
                    'party': 'R',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://garretgraves.house.gov'
                }
            ],
            'ME': [
                {
                    'name': 'Chellie Pingree', gender: 'F',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2009-present',
                    'website': 'https://pingree.house.gov'
                },
                {
                    'name': 'Jared Golden', gender: 'M',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://golden.house.gov'
                }
            ],
            'MD': [
                {
                    'name': 'Andy Harris', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://harris.house.gov'
                },
                {
                    'name': 'Dutch Ruppersberger', gender: 'M',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2003-present',
                    'website': 'https://ruppersberger.house.gov'
                },
                {
                    'name': 'John Sarbanes', gender: 'M',
                    'party': 'D',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2007-present',
                    'website': 'https://sarbanes.house.gov'
                },
                {
                    'name': 'Glenn Ivey', gender: 'M',
                    'party': 'D',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://ivey.house.gov'
                },
                {
                    'name': 'Steny Hoyer', gender: 'M',
                    'party': 'D',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '1981-present',
                    'website': 'https://hoyer.house.gov'
                },
                {
                    'name': 'David Trone', gender: 'M',
                    'party': 'D',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://trone.house.gov'
                },
                {
                    'name': 'Kweisi Mfume', gender: 'M',
                    'party': 'D',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2020-present',
                    'website': 'https://mfume.house.gov'
                },
                {
                    'name': 'Jamie Raskin', gender: 'M',
                    'party': 'D',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://raskin.house.gov'
                }
            ],
            'MA': [
                {
                    'name': 'Richard Neal', gender: 'M',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '1989-present',
                    'website': 'https://neal.house.gov'
                },
                {
                    'name': 'Jim McGovern', gender: 'M',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '1997-present',
                    'website': 'https://mcgovern.house.gov'
                },
                {
                    'name': 'Lori Trahan', gender: 'F',
                    'party': 'D',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://trahan.house.gov'
                },
                {
                    'name': 'Jake Auchincloss', gender: 'M',
                    'party': 'D',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://auchincloss.house.gov'
                },
                {
                    'name': 'Katherine Clark', gender: 'F',
                    'party': 'D',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://katherineclark.house.gov'
                },
                {
                    'name': 'Seth Moulton', gender: 'M',
                    'party': 'D',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://moulton.house.gov'
                },
                {
                    'name': 'Ayanna Pressley', gender: 'F',
                    'party': 'D',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://pressley.house.gov'
                },
                {
                    'name': 'Stephen Lynch', gender: 'M',
                    'party': 'D',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2001-present',
                    'website': 'https://lynch.house.gov'
                },
                {
                    'name': 'Bill Keating', gender: 'M',
                    'party': 'D',
                    'district': 9,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://keating.house.gov'
                }
            ],
            'MI': [
                {
                    'name': 'Jack Bergman', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://bergman.house.gov'
                },
                {
                    'name': 'John Moolenaar', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://moolenaar.house.gov'
                },
                {
                    'name': 'Hillary Scholten', gender: 'F',
                    'party': 'D',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://scholten.house.gov'
                },
                {
                    'name': 'Bill Huizenga', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://huizenga.house.gov'
                },
                {
                    'name': 'Tim Walberg', gender: 'M',
                    'party': 'R',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://walberg.house.gov'
                },
                {
                    'name': 'Debbie Dingell', gender: 'F',
                    'party': 'D',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://debbiedingell.house.gov'
                },
                {
                    'name': 'Elissa Slotkin', gender: 'F',
                    'party': 'D',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://slotkin.house.gov'
                },
                {
                    'name': 'Dan Kildee', gender: 'M',
                    'party': 'D',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://dankildee.house.gov'
                },
                {
                    'name': 'Lisa McClain', gender: 'F',
                    'party': 'R',
                    'district': 9,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://mcclain.house.gov'
                },
                {
                    'name': 'John James', gender: 'M',
                    'party': 'R',
                    'district': 10,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://james.house.gov'
                },
                {
                    'name': 'Haley Stevens', gender: 'F',
                    'party': 'D',
                    'district': 11,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://stevens.house.gov'
                },
                {
                    'name': 'Rashida Tlaib', gender: 'F',
                    'party': 'D',
                    'district': 12,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://tlaib.house.gov'
                },
                {
                    'name': 'Shri Thanedar', gender: 'M',
                    'party': 'D',
                    'district': 13,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://thanedar.house.gov'
                }
            ],
            'MN': [
                {
                    'name': 'Brad Finstad', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2022-present',
                    'website': 'https://finstad.house.gov'
                },
                {
                    'name': 'Angie Craig', gender: 'F',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://craig.house.gov'
                },
                {
                    'name': 'Dean Phillips', gender: 'M',
                    'party': 'D',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://phillips.house.gov'
                },
                {
                    'name': 'Betty McCollum', gender: 'F',
                    'party': 'D',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2001-present',
                    'website': 'https://mccollum.house.gov'
                },
                {
                    'name': 'Ilhan Omar', gender: 'F',
                    'party': 'D',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://omar.house.gov'
                },
                {
                    'name': 'Tom Emmer', gender: 'M',
                    'party': 'R',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://emmer.house.gov'
                },
                {
                    'name': 'Michelle Fischbach', gender: 'F',
                    'party': 'R',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://fischbach.house.gov'
                },
                {
                    'name': 'Pete Stauber', gender: 'M',
                    'party': 'R',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://stauber.house.gov'
                }
            ],
            'MS': [
                {
                    'name': 'Trent Kelly', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://trentkelly.house.gov'
                },
                {
                    'name': 'Bennie Thompson', gender: 'M',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '1993-present',
                    'website': 'https://benniethompson.house.gov'
                },
                {
                    'name': 'Michael Guest', gender: 'M',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://guest.house.gov'
                },
                {
                    'name': 'Mike Ezell', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://ezell.house.gov'
                }
            ],
            'MO': [
                {
                    'name': 'Cori Bush', gender: 'F',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://bush.house.gov'
                },
                {
                    'name': 'Ann Wagner', gender: 'F',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://wagner.house.gov'
                },
                {
                    'name': 'Blaine Luetkemeyer', gender: 'M',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2009-present',
                    'website': 'https://luetkemeyer.house.gov'
                },
                {
                    'name': 'Mark Alford', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://alford.house.gov'
                },
                {
                    'name': 'Emanuel Cleaver', gender: 'M',
                    'party': 'D',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2005-present',
                    'website': 'https://cleaver.house.gov'
                },
                {
                    'name': 'Sam Graves', gender: 'M',
                    'party': 'R',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2001-present',
                    'website': 'https://graves.house.gov'
                },
                {
                    'name': 'Eric Burlison', gender: 'M',
                    'party': 'R',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://burlison.house.gov'
                },
                {
                    'name': 'Jason Smith', gender: 'M',
                    'party': 'R',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://jasonsmith.house.gov'
                }
            ],
            'MT': [
                {
                    'name': 'Ryan Zinke', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://zinke.house.gov'
                },
                {
                    'name': 'Matt Rosendale', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://rosendale.house.gov'
                }
            ],
            'NE': [
                {
                    'name': 'Mike Flood', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2022-present',
                    'website': 'https://flood.house.gov'
                },
                {
                    'name': 'Don Bacon', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://bacon.house.gov'
                },
                {
                    'name': 'Adrian Smith', gender: 'M',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2007-present',
                    'website': 'https://adriansmith.house.gov'
                }
            ],
            'NV': [
                {
                    'name': 'Dina Titus', gender: 'F',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://titus.house.gov'
                },
                {
                    'name': 'Mark Amodei', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://amodei.house.gov'
                },
                {
                    'name': 'Susie Lee', gender: 'F',
                    'party': 'D',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://susielee.house.gov'
                },
                {
                    'name': 'Steven Horsford', gender: 'M',
                    'party': 'D',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://horsford.house.gov'
                }
            ],
            'NH': [
                {
                    'name': 'Chris Pappas', gender: 'M',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://pappas.house.gov'
                },
                {
                    'name': 'Ann McLane Kuster',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://kuster.house.gov'
                }
            ],
            'NJ': [
                {
                    'name': 'Donald Norcross', gender: 'M',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2014-present',
                    'website': 'https://norcross.house.gov'
                },
                {
                    'name': 'Jeff Van Drew', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://vandrew.house.gov'
                },
                {
                    'name': 'Andy Kim', gender: 'M',
                    'party': 'D',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://kim.house.gov'
                },
                {
                    'name': 'Chris Smith', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '1981-present',
                    'website': 'https://chrissmith.house.gov'
                },
                {
                    'name': 'Josh Gottheimer', gender: 'M',
                    'party': 'D',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://gottheimer.house.gov'
                },
                {
                    'name': 'Frank Pallone', gender: 'M',
                    'party': 'D',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '1988-present',
                    'website': 'https://pallone.house.gov'
                },
                {
                    'name': 'Tom Malinowski',
                    'party': 'D',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://malinowski.house.gov'
                },
                {
                    'name': 'Rob Menendez', gender: 'M',
                    'party': 'D',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://robmenendez.house.gov'
                },
                {
                    'name': 'Bill Pascrell', gender: 'M',
                    'party': 'D',
                    'district': 9,
                    'termEnd': '2025',
                    'tenure': '1997-present',
                    'website': 'https://pascrell.house.gov'
                },
                {
                    'name': 'Donald Payne Jr.', gender: 'M',
                    'party': 'D',
                    'district': 10,
                    'termEnd': '2025',
                    'tenure': '2012-present',
                    'website': 'https://payne.house.gov'
                },
                {
                    'name': 'Mikie Sherrill', gender: 'F',
                    'party': 'D',
                    'district': 11,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://sherrill.house.gov'
                },
                {
                    'name': 'Bonnie Watson Coleman', gender: 'F',
                    'party': 'D',
                    'district': 12,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://watsoncoleman.house.gov'
                }
            ],
            'NM': [
                {
                    'name': 'Melanie Stansbury', gender: 'F',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://stansbury.house.gov'
                },
                {
                    'name': 'Gabe Vasquez', gender: 'M',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://vasquez.house.gov'
                },
                {
                    'name': 'Teresa Leger Fernandez', gender: 'F',
                    'party': 'D',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://fernandez.house.gov'
                }
            ],
            'NY': [
                {
                    'name': 'Nick LaLota', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://lalota.house.gov'
                },
                {
                    'name': 'Andrew Garbarino', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://garbarino.house.gov'
                },
                {
                    'name': 'George Santos', gender: 'M',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://santos.house.gov'
                },
                {
                    'name': 'Anthony D\'Esposito',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://desposito.house.gov'
                },
                {
                    'name': 'Gregory Meeks', gender: 'M',
                    'party': 'D',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '1998-present',
                    'website': 'https://meeks.house.gov'
                },
                {
                    'name': 'Grace Meng', gender: 'F',
                    'party': 'D',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://meng.house.gov'
                },
                {
                    'name': 'Nydia Velázquez', gender: 'F',
                    'party': 'D',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '1993-present',
                    'website': 'https://velazquez.house.gov'
                },
                {
                    'name': 'Hakeem Jeffries', gender: 'M',
                    'party': 'D',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://jeffries.house.gov'
                },
                {
                    'name': 'Yvette Clarke', gender: 'F',
                    'party': 'D',
                    'district': 9,
                    'termEnd': '2025',
                    'tenure': '2007-present',
                    'website': 'https://clarke.house.gov'
                },
                {
                    'name': 'Dan Goldman', gender: 'M',
                    'party': 'D',
                    'district': 10,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://goldman.house.gov'
                },
                {
                    'name': 'Nicole Malliotakis', gender: 'F',
                    'party': 'R',
                    'district': 11,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://malliotakis.house.gov'
                },
                {
                    'name': 'Jerry Nadler', gender: 'M',
                    'party': 'D',
                    'district': 12,
                    'termEnd': '2025',
                    'tenure': '1992-present',
                    'website': 'https://nadler.house.gov'
                },
                {
                    'name': 'Adriano Espaillat', gender: 'M',
                    'party': 'D',
                    'district': 13,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://espaillat.house.gov'
                },
                {
                    'name': 'Alexandria Ocasio-Cortez', gender: 'F',
                    'party': 'D',
                    'district': 14,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://ocasio-cortez.house.gov'
                },
                {
                    'name': 'Ritchie Torres', gender: 'M',
                    'party': 'D',
                    'district': 15,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://torres.house.gov'
                },
                {
                    'name': 'Jamaal Bowman', gender: 'M',
                    'party': 'D',
                    'district': 16,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://bowman.house.gov'
                },
                {
                    'name': 'Mike Lawler', gender: 'M',
                    'party': 'R',
                    'district': 17,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://lawler.house.gov'
                },
                {
                    'name': 'Pat Ryan', gender: 'M',
                    'party': 'D',
                    'district': 18,
                    'termEnd': '2025',
                    'tenure': '2022-present',
                    'website': 'https://ryan.house.gov'
                },
                {
                    'name': 'Marcus Molinaro',
                    'party': 'R',
                    'district': 19,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://molinaro.house.gov'
                },
                {
                    'name': 'Paul Tonko', gender: 'M',
                    'party': 'D',
                    'district': 20,
                    'termEnd': '2025',
                    'tenure': '2009-present',
                    'website': 'https://tonko.house.gov'
                },
                {
                    'name': 'Elise Stefanik', gender: 'F',
                    'party': 'R',
                    'district': 21,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://stefanik.house.gov'
                },
                {
                    'name': 'Brandon Williams', gender: 'M',
                    'party': 'R',
                    'district': 22,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://williams.house.gov'
                },
                {
                    'name': 'Nick Langworthy', gender: 'M',
                    'party': 'R',
                    'district': 23,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://langworthy.house.gov'
                },
                {
                    'name': 'Claudia Tenney', gender: 'F',
                    'party': 'R',
                    'district': 24,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://tenney.house.gov'
                },
                {
                    'name': 'Joseph Morelle',
                    'party': 'D',
                    'district': 25,
                    'termEnd': '2025',
                    'tenure': '2018-present',
                    'website': 'https://morelle.house.gov'
                },
                {
                    'name': 'Brian Higgins', gender: 'M',
                    'party': 'D',
                    'district': 26,
                    'termEnd': '2025',
                    'tenure': '2005-present',
                    'website': 'https://higgins.house.gov'
                }
            ],
            'NC': [
                {
                    'name': 'Don Davis', gender: 'M',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://davis.house.gov'
                },
                {
                    'name': 'Deborah Ross', gender: 'F',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://ross.house.gov'
                },
                {
                    'name': 'Greg Murphy', gender: 'M',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://murphy.house.gov'
                },
                {
                    'name': 'Valerie Foushee', gender: 'F',
                    'party': 'D',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://foushee.house.gov'
                },
                {
                    'name': 'Virginia Foxx', gender: 'F',
                    'party': 'R',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2005-present',
                    'website': 'https://foxx.house.gov'
                },
                {
                    'name': 'Kathy Manning', gender: 'F',
                    'party': 'D',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://manning.house.gov'
                },
                {
                    'name': 'David Rouzer', gender: 'M',
                    'party': 'R',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://rouzer.house.gov'
                },
                {
                    'name': 'Richard Hudson', gender: 'M',
                    'party': 'R',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://hudson.house.gov'
                },
                {
                    'name': 'Dan Bishop', gender: 'M',
                    'party': 'R',
                    'district': 9,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://danbishop.house.gov'
                },
                {
                    'name': 'Patrick McHenry', gender: 'M',
                    'party': 'R',
                    'district': 10,
                    'termEnd': '2025',
                    'tenure': '2005-present',
                    'website': 'https://mchenry.house.gov'
                },
                {
                    'name': 'Chuck Edwards', gender: 'M',
                    'party': 'R',
                    'district': 11,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://edwards.house.gov'
                },
                {
                    'name': 'Alma Adams', gender: 'F',
                    'party': 'D',
                    'district': 12,
                    'termEnd': '2025',
                    'tenure': '2014-present',
                    'website': 'https://adams.house.gov'
                },
                {
                    'name': 'Wiley Nickel', gender: 'M',
                    'party': 'D',
                    'district': 13,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://nickel.house.gov'
                },
                {
                    'name': 'Jeff Jackson', gender: 'M',
                    'party': 'D',
                    'district': 14,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://jeffjackson.house.gov'
                }
            ],
            'ND': [
                {
                    'name': 'Kelly Armstrong', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://armstrong.house.gov'
                }
            ],
            'OH': [
                {
                    'name': 'Greg Landsman', gender: 'M',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://landsman.house.gov'
                },
                {
                    'name': 'Brad Wenstrup', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://wenstrup.house.gov'
                },
                {
                    'name': 'Joyce Beatty', gender: 'F',
                    'party': 'D',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://beatty.house.gov'
                },
                {
                    'name': 'Jim Jordan', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2007-present',
                    'website': 'https://jordan.house.gov'
                },
                {
                    'name': 'Bob Latta', gender: 'M',
                    'party': 'R',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2007-present',
                    'website': 'https://latta.house.gov'
                },
                {
                    'name': 'Michael Rulli',
                    'party': 'R',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2024-present',
                    'website': 'https://rulli.house.gov'
                },
                {
                    'name': 'Max Miller', gender: 'M',
                    'party': 'R',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://maxmiller.house.gov'
                },
                {
                    'name': 'Warren Davidson', gender: 'M',
                    'party': 'R',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2016-present',
                    'website': 'https://davidson.house.gov'
                },
                {
                    'name': 'Marcy Kaptur', gender: 'F',
                    'party': 'D',
                    'district': 9,
                    'termEnd': '2025',
                    'tenure': '1983-present',
                    'website': 'https://kaptur.house.gov'
                },
                {
                    'name': 'Mike Turner', gender: 'M',
                    'party': 'R',
                    'district': 10,
                    'termEnd': '2025',
                    'tenure': '2003-present',
                    'website': 'https://turner.house.gov'
                },
                {
                    'name': 'Shontel Brown', gender: 'F',
                    'party': 'D',
                    'district': 11,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://shontelbrown.house.gov'
                },
                {
                    'name': 'Troy Balderson', gender: 'M',
                    'party': 'R',
                    'district': 12,
                    'termEnd': '2025',
                    'tenure': '2018-present',
                    'website': 'https://balderson.house.gov'
                },
                {
                    'name': 'Emilia Sykes', gender: 'F',
                    'party': 'D',
                    'district': 13,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://sykes.house.gov'
                },
                {
                    'name': 'David Joyce', gender: 'M',
                    'party': 'R',
                    'district': 14,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://joyce.house.gov'
                },
                {
                    'name': 'Mike Carey', gender: 'M',
                    'party': 'R',
                    'district': 15,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://carey.house.gov'
                }
            ],
            'OK': [
                {
                    'name': 'Kevin Hern', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2018-present',
                    'website': 'https://hern.house.gov'
                },
                {
                    'name': 'Josh Brecheen', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://brecheen.house.gov'
                },
                {
                    'name': 'Frank Lucas', gender: 'M',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '1994-present',
                    'website': 'https://lucas.house.gov'
                },
                {
                    'name': 'Tom Cole', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2003-present',
                    'website': 'https://cole.house.gov'
                },
                {
                    'name': 'Stephanie Bice', gender: 'F',
                    'party': 'R',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://bice.house.gov'
                }
            ],
            'OR': [
                {
                    'name': 'Suzanne Bonamici', gender: 'F',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2012-present',
                    'website': 'https://bonamici.house.gov'
                },
                {
                    'name': 'Cliff Bentz', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://bentz.house.gov'
                },
                {
                    'name': 'Earl Blumenauer', gender: 'M',
                    'party': 'D',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '1996-present',
                    'website': 'https://blumenauer.house.gov'
                },
                {
                    'name': 'Val Hoyle', gender: 'F',
                    'party': 'D',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://hoyle.house.gov'
                },
                {
                    'name': 'Lori Chavez-DeRemer', gender: 'F',
                    'party': 'R',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://chavez-deremer.house.gov'
                },
                {
                    'name': 'Andrea Salinas', gender: 'F',
                    'party': 'D',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://salinas.house.gov'
                }
            ],
            'PA': [
                {
                    'name': 'Brian Fitzpatrick', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://fitzpatrick.house.gov'
                },
                {
                    'name': 'Brendan Boyle', gender: 'M',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://boyle.house.gov'
                },
                {
                    'name': 'Dwight Evans', gender: 'M',
                    'party': 'D',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2016-present',
                    'website': 'https://evans.house.gov'
                },
                {
                    'name': 'Madeleine Dean', gender: 'F',
                    'party': 'D',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://dean.house.gov'
                },
                {
                    'name': 'Mary Gay Scanlon', gender: 'F',
                    'party': 'D',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2018-present',
                    'website': 'https://scanlon.house.gov'
                },
                {
                    'name': 'Chrissy Houlahan', gender: 'F',
                    'party': 'D',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://houlahan.house.gov'
                },
                {
                    'name': 'Susan Wild', gender: 'F',
                    'party': 'D',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2018-present',
                    'website': 'https://wild.house.gov'
                },
                {
                    'name': 'Matt Cartwright', gender: 'M',
                    'party': 'D',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://cartwright.house.gov'
                },
                {
                    'name': 'Dan Meuser', gender: 'M',
                    'party': 'R',
                    'district': 9,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://meuser.house.gov'
                },
                {
                    'name': 'Scott Perry', gender: 'M',
                    'party': 'R',
                    'district': 10,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://perry.house.gov'
                },
                {
                    'name': 'Lloyd Smucker', gender: 'M',
                    'party': 'R',
                    'district': 11,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://smucker.house.gov'
                },
                {
                    'name': 'Summer Lee', gender: 'F',
                    'party': 'D',
                    'district': 12,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://summerlee.house.gov'
                },
                {
                    'name': 'John Joyce', gender: 'M',
                    'party': 'R',
                    'district': 13,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://johnjoyce.house.gov'
                },
                {
                    'name': 'Guy Reschenthaler', gender: 'M',
                    'party': 'R',
                    'district': 14,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://reschenthaler.house.gov'
                },
                {
                    'name': 'Glenn Thompson', gender: 'M',
                    'party': 'R',
                    'district': 15,
                    'termEnd': '2025',
                    'tenure': '2009-present',
                    'website': 'https://thompson.house.gov'
                },
                {
                    'name': 'Mike Kelly', gender: 'M',
                    'party': 'R',
                    'district': 16,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://kelly.house.gov'
                },
                {
                    'name': 'Chris Deluzio', gender: 'M',
                    'party': 'D',
                    'district': 17,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://deluzio.house.gov'
                }
            ],
            'RI': [
                {
                    'name': 'David Cicilline', gender: 'M',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://cicilline.house.gov'
                },
                {
                    'name': 'Seth Magaziner', gender: 'M',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://magaziner.house.gov'
                }
            ],
            'SC': [
                {
                    'name': 'Nancy Mace', gender: 'F',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://mace.house.gov'
                },
                {
                    'name': 'Joe Wilson', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2001-present',
                    'website': 'https://joewilson.house.gov'
                },
                {
                    'name': 'Jeff Duncan', gender: 'M',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://jeffduncan.house.gov'
                },
                {
                    'name': 'William Timmons', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://timmons.house.gov'
                },
                {
                    'name': 'Ralph Norman', gender: 'M',
                    'party': 'R',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://norman.house.gov'
                },
                {
                    'name': 'James Clyburn',
                    'party': 'D',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '1993-present',
                    'website': 'https://clyburn.house.gov'
                },
                {
                    'name': 'Russell Fry', gender: 'M',
                    'party': 'R',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://fry.house.gov'
                }
            ],
            'SD': [
                {
                    'name': 'Dusty Johnson', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://dustyjohnson.house.gov'
                }
            ],
            'TN': [
                {
                    'name': 'Diana Harshbarger', gender: 'F',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://harshbarger.house.gov'
                },
                {
                    'name': 'Tim Burchett', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://burchett.house.gov'
                },
                {
                    'name': 'Chuck Fleischmann', gender: 'M',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://fleischmann.house.gov'
                },
                {
                    'name': 'Scott DesJarlais', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://desjarlais.house.gov'
                },
                {
                    'name': 'Andy Ogles', gender: 'M',
                    'party': 'R',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://ogles.house.gov'
                },
                {
                    'name': 'John Rose', gender: 'M',
                    'party': 'R',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://johnrose.house.gov'
                },
                {
                    'name': 'Mark Green', gender: 'M',
                    'party': 'R',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://markgreen.house.gov'
                },
                {
                    'name': 'David Kustoff', gender: 'M',
                    'party': 'R',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://kustoff.house.gov'
                },
                {
                    'name': 'Steve Cohen', gender: 'M',
                    'party': 'D',
                    'district': 9,
                    'termEnd': '2025',
                    'tenure': '2007-present',
                    'website': 'https://cohen.house.gov'
                }
            ],
            'TX': [
                {
                    'name': 'Nathaniel Moran', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://moran.house.gov'
                },
                {
                    'name': 'Dan Crenshaw', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://crenshaw.house.gov'
                },
                {
                    'name': 'Keith Self', gender: 'M',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://self.house.gov'
                },
                {
                    'name': 'Pat Fallon', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://fallon.house.gov'
                },
                {
                    'name': 'Lance Gooden', gender: 'M',
                    'party': 'R',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://gooden.house.gov'
                },
                {
                    'name': 'Jake Ellzey', gender: 'M',
                    'party': 'R',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://ellzey.house.gov'
                },
                {
                    'name': 'Lizzie Fletcher', gender: 'F',
                    'party': 'D',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://fletcher.house.gov'
                },
                {
                    'name': 'Morgan Luttrell', gender: 'M',
                    'party': 'R',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://luttrell.house.gov'
                },
                {
                    'name': 'Al Green', gender: 'M',
                    'party': 'D',
                    'district': 9,
                    'termEnd': '2025',
                    'tenure': '2005-present',
                    'website': 'https://algreen.house.gov'
                },
                {
                    'name': 'Michael McCaul', gender: 'M',
                    'party': 'R',
                    'district': 10,
                    'termEnd': '2025',
                    'tenure': '2005-present',
                    'website': 'https://mccaul.house.gov'
                },
                {
                    'name': 'August Pfluger', gender: 'M',
                    'party': 'R',
                    'district': 11,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://pfluger.house.gov'
                },
                {
                    'name': 'Kay Granger', gender: 'F',
                    'party': 'R',
                    'district': 12,
                    'termEnd': '2025',
                    'tenure': '1997-present',
                    'website': 'https://kaygranger.house.gov'
                },
                {
                    'name': 'Ronny Jackson', gender: 'M',
                    'party': 'R',
                    'district': 13,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://jackson.house.gov'
                },
                {
                    'name': 'Randy Weber', gender: 'M',
                    'party': 'R',
                    'district': 14,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://weber.house.gov'
                },
                {
                    'name': 'Monica De La Cruz', gender: 'F',
                    'party': 'R',
                    'district': 15,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://delacruz.house.gov'
                },
                {
                    'name': 'Veronica Escobar', gender: 'F',
                    'party': 'D',
                    'district': 16,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://escobar.house.gov'
                },
                {
                    'name': 'Pete Sessions', gender: 'M',
                    'party': 'R',
                    'district': 17,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://sessions.house.gov'
                },
                {
                    'name': 'Sheila Jackson Lee', gender: 'F',
                    'party': 'D',
                    'district': 18,
                    'termEnd': '2025',
                    'tenure': '1995-present',
                    'website': 'https://jacksonlee.house.gov'
                },
                {
                    'name': 'Jodey Arrington', gender: 'M',
                    'party': 'R',
                    'district': 19,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://arrington.house.gov'
                },
                {
                    'name': 'Joaquin Castro', gender: 'M',
                    'party': 'D',
                    'district': 20,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://castro.house.gov'
                },
                {
                    'name': 'Chip Roy', gender: 'M',
                    'party': 'R',
                    'district': 21,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://roy.house.gov'
                },
                {
                    'name': 'Troy Nehls', gender: 'M',
                    'party': 'R',
                    'district': 22,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://nehls.house.gov'
                },
                {
                    'name': 'Tony Gonzales', gender: 'M',
                    'party': 'R',
                    'district': 23,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://gonzales.house.gov'
                },
                {
                    'name': 'Beth Van Duyne', gender: 'F',
                    'party': 'R',
                    'district': 24,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://vanduyne.house.gov'
                },
                {
                    'name': 'Roger Williams', gender: 'M',
                    'party': 'R',
                    'district': 25,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://williams.house.gov'
                },
                {
                    'name': 'Michael Burgess', gender: 'M',
                    'party': 'R',
                    'district': 26,
                    'termEnd': '2025',
                    'tenure': '2003-present',
                    'website': 'https://burgess.house.gov'
                },
                {
                    'name': 'Michael Cloud', gender: 'M',
                    'party': 'R',
                    'district': 27,
                    'termEnd': '2025',
                    'tenure': '2018-present',
                    'website': 'https://cloud.house.gov'
                },
                {
                    'name': 'Henry Cuellar', gender: 'M',
                    'party': 'D',
                    'district': 28,
                    'termEnd': '2025',
                    'tenure': '2005-present',
                    'website': 'https://cuellar.house.gov'
                },
                {
                    'name': 'Sylvia Garcia', gender: 'F',
                    'party': 'D',
                    'district': 29,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://sylviagarcia.house.gov'
                },
                {
                    'name': 'Jasmine Crockett', gender: 'F',
                    'party': 'D',
                    'district': 30,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://crockett.house.gov'
                },
                {
                    'name': 'John Carter', gender: 'M',
                    'party': 'R',
                    'district': 31,
                    'termEnd': '2025',
                    'tenure': '2003-present',
                    'website': 'https://carter.house.gov'
                },
                {
                    'name': 'Colin Allred', gender: 'M',
                    'party': 'D',
                    'district': 32,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://allred.house.gov'
                },
                {
                    'name': 'Marc Veasey', gender: 'M',
                    'party': 'D',
                    'district': 33,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://veasey.house.gov'
                },
                {
                    'name': 'Vicente Gonzalez', gender: 'M',
                    'party': 'D',
                    'district': 34,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://gonzalez.house.gov'
                },
                {
                    'name': 'Greg Casar', gender: 'M',
                    'party': 'D',
                    'district': 35,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://casar.house.gov'
                },
                {
                    'name': 'Brian Babin', gender: 'M',
                    'party': 'R',
                    'district': 36,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://babin.house.gov'
                },
                {
                    'name': 'Lloyd Doggett', gender: 'M',
                    'party': 'D',
                    'district': 37,
                    'termEnd': '2025',
                    'tenure': '1995-present',
                    'website': 'https://doggett.house.gov'
                },
                {
                    'name': 'Wesley Hunt',
                    'party': 'R',
                    'district': 38,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://hunt.house.gov'
                }
            ],
            'UT': [
                {
                    'name': 'Blake Moore', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://blakemoore.house.gov'
                },
                {
                    'name': 'Chris Stewart', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://stewart.house.gov'
                },
                {
                    'name': 'John Curtis', gender: 'M',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://curtis.house.gov'
                },
                {
                    'name': 'Burgess Owens', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://owens.house.gov'
                }
            ],
            'VT': [
                {
                    'name': 'Becca Balint', gender: 'F',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://balint.house.gov'
                }
            ],
            'VA': [
                {
                    'name': 'Rob Wittman', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2007-present',
                    'website': 'https://wittman.house.gov'
                },
                {
                    'name': 'Jen Kiggans', gender: 'F',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://kiggans.house.gov'
                },
                {
                    'name': 'Bobby Scott', gender: 'M',
                    'party': 'D',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '1993-present',
                    'website': 'https://bobbyscott.house.gov'
                },
                {
                    'name': 'Jennifer McClellan', gender: 'F',
                    'party': 'D',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://mcclellan.house.gov'
                },
                {
                    'name': 'Bob Good', gender: 'M',
                    'party': 'R',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://good.house.gov'
                },
                {
                    'name': 'Ben Cline', gender: 'M',
                    'party': 'R',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://cline.house.gov'
                },
                {
                    'name': 'Abigail Spanberger', gender: 'F',
                    'party': 'D',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://spanberger.house.gov'
                },
                {
                    'name': 'Don Beyer', gender: 'M',
                    'party': 'D',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://beyer.house.gov'
                },
                {
                    'name': 'Morgan Griffith', gender: 'M',
                    'party': 'R',
                    'district': 9,
                    'termEnd': '2025',
                    'tenure': '2011-present',
                    'website': 'https://morgangriffith.house.gov'
                },
                {
                    'name': 'Jennifer Wexton', gender: 'F',
                    'party': 'D',
                    'district': 10,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://wexton.house.gov'
                },
                {
                    'name': 'Gerry Connolly', gender: 'M',
                    'party': 'D',
                    'district': 11,
                    'termEnd': '2025',
                    'tenure': '2009-present',
                    'website': 'https://connolly.house.gov'
                }
            ],
            'WA': [
                {
                    'name': 'Suzan DelBene', gender: 'F',
                    'party': 'D',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2012-present',
                    'website': 'https://delbene.house.gov'
                },
                {
                    'name': 'Rick Larsen', gender: 'M',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2001-present',
                    'website': 'https://larsen.house.gov'
                },
                {
                    'name': 'Marie Gluesenkamp Perez', gender: 'F',
                    'party': 'D',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://gluesenkampperez.house.gov'
                },
                {
                    'name': 'Dan Newhouse', gender: 'M',
                    'party': 'R',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://newhouse.house.gov'
                },
                {
                    'name': 'Cathy McMorris Rodgers', gender: 'F',
                    'party': 'R',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2005-present',
                    'website': 'https://mcmorris.house.gov'
                },
                {
                    'name': 'Derek Kilmer', gender: 'M',
                    'party': 'D',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://kilmer.house.gov'
                },
                {
                    'name': 'Pramila Jayapal', gender: 'F',
                    'party': 'D',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://jayapal.house.gov'
                },
                {
                    'name': 'Kim Schrier', gender: 'F',
                    'party': 'D',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://schrier.house.gov'
                },
                {
                    'name': 'Adam Smith', gender: 'M',
                    'party': 'D',
                    'district': 9,
                    'termEnd': '2025',
                    'tenure': '1997-present',
                    'website': 'https://adamsmith.house.gov'
                },
                {
                    'name': 'Marilyn Strickland', gender: 'F',
                    'party': 'D',
                    'district': 10,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://strickland.house.gov'
                }
            ],
            'WV': [
                {
                    'name': 'Carol Miller', gender: 'F',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://miller.house.gov'
                },
                {
                    'name': 'Alex Mooney', gender: 'M',
                    'party': 'R',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://mooney.house.gov'
                }
            ],
            'WI': [
                {
                    'name': 'Bryan Steil', gender: 'M',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2019-present',
                    'website': 'https://steil.house.gov'
                },
                {
                    'name': 'Mark Pocan', gender: 'M',
                    'party': 'D',
                    'district': 2,
                    'termEnd': '2025',
                    'tenure': '2013-present',
                    'website': 'https://pocan.house.gov'
                },
                {
                    'name': 'Derrick Van Orden', gender: 'M',
                    'party': 'R',
                    'district': 3,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://vanorden.house.gov'
                },
                {
                    'name': 'Gwen Moore', gender: 'F',
                    'party': 'D',
                    'district': 4,
                    'termEnd': '2025',
                    'tenure': '2005-present',
                    'website': 'https://gwenmoore.house.gov'
                },
                {
                    'name': 'Scott Fitzgerald', gender: 'M',
                    'party': 'R',
                    'district': 5,
                    'termEnd': '2025',
                    'tenure': '2021-present',
                    'website': 'https://fitzgerald.house.gov'
                },
                {
                    'name': 'Glenn Grothman', gender: 'M',
                    'party': 'R',
                    'district': 6,
                    'termEnd': '2025',
                    'tenure': '2015-present',
                    'website': 'https://grothman.house.gov'
                },
                {
                    'name': 'Tom Tiffany', gender: 'M',
                    'party': 'R',
                    'district': 7,
                    'termEnd': '2025',
                    'tenure': '2020-present',
                    'website': 'https://tiffany.house.gov'
                },
                {
                    'name': 'Mike Gallagher', gender: 'M',
                    'party': 'R',
                    'district': 8,
                    'termEnd': '2025',
                    'tenure': '2017-present',
                    'website': 'https://gallagher.house.gov'
                }
            ],
            'WY': [
                {
                    'name': 'Harriet Hageman', gender: 'F',
                    'party': 'R',
                    'district': 1,
                    'termEnd': '2025',
                    'tenure': '2023-present',
                    'website': 'https://hageman.house.gov'
                }
            ]
        };
    }

    /**
     * Get complete government information for a state
     */
    getStateGovernment(stateCode) {
        const governor = this.getGovernor(stateCode);
        const senators = this.getSenators(stateCode);
        const representatives = this.getHouseRepresentatives(stateCode);
        
        // Calculate age statistics
        const allOfficials = [];
        if (governor) allOfficials.push(governor);
        allOfficials.push(...senators);
        if (representatives.representatives) {
            allOfficials.push(...representatives.representatives);
        }
        
        const ages = allOfficials
            .map(o => o.currentAge)
            .filter(age => age !== null && age !== undefined);
        
        const averageAge = ages.length > 0 
            ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length)
            : null;
        
        return {
            website: this.stateWebsites[stateCode],
            governor,
            senators,
            houseSeats: this.houseSeats[stateCode],
            representatives,
            legislature: this.stateLegislatures[stateCode],
            partyControl: this.getPartyControl(stateCode),
            ageStatistics: {
                averageAge,
                totalOfficials: allOfficials.length,
                withAgeData: ages.length
            }
        };
    }

    /**
     * Get party control summary
     */
    getPartyControl(stateCode) {
        const gov = this.governors[stateCode];
        const senators = this.senators[stateCode];
        const legislature = this.stateLegislatures[stateCode];

        const senatorParties = senators.map(s => s.party);
        const demSenators = senatorParties.filter(p => p === 'D').length;
        const repSenators = senatorParties.filter(p => p === 'R').length;
        const indSenators = senatorParties.filter(p => p === 'I').length;

        return {
            governor: gov.party,
            senateMajority: demSenators > repSenators ? 'D' : repSenators > demSenators ? 'R' : 'Split',
            senators: { D: demSenators, R: repSenators, I: indSenators },
            stateLegislature: legislature.trifecta,
            trifecta: legislature.trifecta // State government trifecta
        };
    }

    /**
     * Get state website
     */
    getStateWebsite(stateCode) {
        return this.stateWebsites[stateCode];
    }

    /**
     * Get governor information with complete age data
     */
    getGovernor(stateCode) {
        const baseData = this.governors[stateCode];
        const ageData = POLITICAL_LEADERS_AGE.governors[stateCode];
        
        if (!baseData) return null;
        
        // Merge age data with base data
        const enrichedData = {
            ...baseData,
            ...(ageData || {}),
            currentAge: ageData?.birthDate ? calculateAge(ageData.birthDate) : ageData?.age || baseData.age,
            generation: ageData?.birthDate ? getGeneration(ageData.birthDate) : null,
            ageColor: ageData?.birthDate ? getAgeColor(calculateAge(ageData.birthDate)) : null,
            formattedAge: ageData?.birthDate ? formatAgeDisplay(ageData.birthDate) : null
        };
        
        // Add tenure information
        if (baseData.tenure) {
            const tenureMatch = baseData.tenure.match(/(\d{4})-/);
            if (tenureMatch) {
                const startYear = tenureMatch[1];
                const tenureData = calculateTenure(`${startYear}-01-01`);
                enrichedData.tenureYears = tenureData?.years;
                enrichedData.tenureFormatted = tenureData?.formatted;
                enrichedData.tenureColor = getTenureColor(tenureData?.years);
                enrichedData.tenureStartDate = `${startYear}-01-01`;
            }
        }
        
        return enrichedData;
    }

    /**
     * Get senators information with complete age data
     */
    getSenators(stateCode) {
        const baseData = this.senators[stateCode];
        const ageData = POLITICAL_LEADERS_AGE.senators[stateCode];
        
        if (!baseData) return [];
        
        return baseData.map((senator, index) => {
            // Find matching senator in age data
            const senatorAgeData = ageData?.find(s => 
                s.name === senator.name || 
                s.name.toLowerCase().includes(senator.name.toLowerCase().split(' ').pop())
            ) || ageData?.[index];
            
            const enrichedSenator = {
                ...senator,
                ...(senatorAgeData || {}),
                currentAge: senatorAgeData?.birthDate ? calculateAge(senatorAgeData.birthDate) : senatorAgeData?.age || senator.age,
                generation: senatorAgeData?.birthDate ? getGeneration(senatorAgeData.birthDate) : null,
                ageColor: senatorAgeData?.birthDate ? getAgeColor(calculateAge(senatorAgeData.birthDate)) : null,
                formattedAge: senatorAgeData?.birthDate ? formatAgeDisplay(senatorAgeData.birthDate) : null
            };
            
            // Add tenure information
            if (senator.tenure) {
                const tenureMatch = senator.tenure.match(/(\d{4})-/);
                if (tenureMatch) {
                    const startYear = tenureMatch[1];
                    const tenureData = calculateTenure(`${startYear}-01-01`);
                    enrichedSenator.tenureYears = tenureData?.years;
                    enrichedSenator.tenureFormatted = tenureData?.formatted;
                    enrichedSenator.tenureColor = getTenureColor(tenureData?.years);
                    enrichedSenator.tenureStartDate = `${startYear}-01-01`;
                }
            }
            
            return enrichedSenator;
        });
    }

    /**
     * Get number of House seats
     */
    getHouseSeats(stateCode) {
        return this.houseSeats[stateCode];
    }

    /**
     * Get state legislature information
     */
    getStateLegislature(stateCode) {
        return this.stateLegislatures[stateCode];
    }

    /**
     * Get House Representatives for a state with complete age data
     */
    getHouseRepresentatives(stateCode) {
        // Use complete house representatives data
        const completeData = COMPLETE_HOUSE_REPRESENTATIVES[stateCode];
        
        if (!completeData || completeData.length === 0) {
            // This shouldn't happen as we have complete data now
            return {
                totalSeats: this.houseSeats[stateCode],
                representatives: [],
                dataAvailable: false,
                message: `Data temporarily unavailable`
            };
        }
        
        // Enrich representative data with calculated fields
        const enrichedReps = completeData.map(rep => {
            const enrichedRep = {
                ...rep,
                currentAge: rep.birthDate ? calculateAge(rep.birthDate) : rep.age,
                generation: rep.birthDate ? getGeneration(rep.birthDate) : null,
                ageColor: rep.birthDate ? getAgeColor(calculateAge(rep.birthDate)) : getAgeColor(rep.age),
                formattedAge: rep.birthDate ? formatAgeDisplay(rep.birthDate) : `${rep.age} years old`,
                tenure: '2023-present' // Current term for all House members
            };
            
            // Add tenure calculation (all House members elected in 2022 for 2023-2025 term)
            const tenureData = calculateTenure('2023-01-03'); // 118th Congress start date
            enrichedRep.tenureYears = tenureData?.years;
            enrichedRep.tenureFormatted = tenureData?.formatted;
            enrichedRep.tenureColor = getTenureColor(tenureData?.years);
            
            return enrichedRep;
        });
        
        return {
            totalSeats: this.houseSeats[stateCode],
            representatives: enrichedReps,
            dataAvailable: true,
            sampleSize: enrichedReps.length,
            message: `All ${enrichedReps.length} representative${enrichedReps.length > 1 ? 's' : ''}`
        };
    }
    
    /**
     * Get political age statistics for all officials
     */
    getPoliticalAgeStats() {
        const allSenators = [];
        const allGovernors = [];
        const allReps = [];
        
        // Collect all senators
        Object.values(POLITICAL_LEADERS_AGE.senators).forEach(senators => {
            allSenators.push(...senators);
        });
        
        // Collect all governors  
        Object.values(POLITICAL_LEADERS_AGE.governors).forEach(governor => {
            allGovernors.push(governor);
        });
        
        // Collect sample house reps
        Object.values(POLITICAL_LEADERS_AGE.houseRepresentatives).forEach(reps => {
            allReps.push(...reps);
        });
        
        const getAvgAge = (group) => {
            const ages = group.map(p => p.age).filter(a => a);
            return Math.round(ages.reduce((sum, age) => sum + age, 0) / ages.length);
        };
        
        return {
            senate: {
                averageAge: getAvgAge(allSenators),
                oldest: Math.max(...allSenators.map(s => s.age || 0)),
                youngest: Math.min(...allSenators.filter(s => s.age).map(s => s.age)),
                total: allSenators.length
            },
            governors: {
                averageAge: getAvgAge(allGovernors),
                oldest: Math.max(...allGovernors.map(g => g.age || 0)),
                youngest: Math.min(...allGovernors.filter(g => g.age).map(g => g.age)),
                total: allGovernors.length
            },
            house: {
                averageAge: getAvgAge(allReps),
                oldest: Math.max(...allReps.map(r => r.age || 0)),
                youngest: Math.min(...allReps.filter(r => r.age).map(r => r.age)),
                sampleSize: allReps.length
            }
        };
    }


    /**
     * Get party statistics for all states
     */
    getPartyStatistics() {
        let stats = {
            governors: { D: 0, R: 0, I: 0 },
            senators: { D: 0, R: 0, I: 0 },
            stateTrifectas: { D: 0, R: 0, Split: 0 }
        };

        Object.values(this.governors).forEach(gov => {
            stats.governors[gov.party]++;
        });

        Object.values(this.senators).forEach(stateSenators => {
            stateSenators.forEach(senator => {
                stats.senators[senator.party]++;
            });
        });

        Object.values(this.stateLegislatures).forEach(leg => {
            if (leg.trifecta === 'D') stats.stateTrifectas.D++;
            else if (leg.trifecta === 'R') stats.stateTrifectas.R++;
            else stats.stateTrifectas.Split++;
        });

        return stats;
    }

    /**
     * Format party with color
     */
    getPartyColor(party) {
        switch(party) {
            case 'D': return '#1E90FF'; // Democratic Blue
            case 'R': return '#DC143C'; // Republican Red
            case 'I': return '#808080'; // Independent Gray
            default: return '#666666';
        }
    }

    /**
     * Get party emoji
     */
    getPartyEmoji(party) {
        switch(party) {
            case 'D': return '🔵';
            case 'R': return '🔴';
            case 'I': return '⚪';
            default: return '⚫';
        }
    }
}

// Create singleton instance
const stateGovernmentService = new StateGovernmentService();

export default stateGovernmentService;